import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n/i18n";
import { useDispatch, useSelector } from "react-redux";
import { getAdminInboxRequest, getAllLeaveRequestsRequest } from "../../../../redux/actions/leaveRequestsActions";
import { getAdminAvanceInboxRequest, getAdminAvanceHistoryRequest } from "../../../../redux/actions/avanceRequestActions";
import { getAdminNoteDeFraisInboxRequest, getAdminNoteDeFraisHistoryRequest } from "../../../../redux/actions/noteDeFraisRequestActions";
import { getAdminMissionInboxRequest, getAdminMissionHistoryRequest, getAllMissionRequestsRequest } from "../../../../redux/actions/missionRequestActions";
import { getAdminAttestationInboxRequest, getAdminAttestationHistoryRequest } from "../../../../redux/actions/attestationsActions";
import { fetchOffboardingsRequest, fetchEtapesRequest } from "../../../../redux/actions/offboardingActions";
import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import { getAllEntitesRequest } from "../../../../redux/actions/entitesActions";
import { API_BASE_URL, deleteRequest, getRequest, putRequest } from "../../../../../api";
import {
    Users,
    Building2,
    TrendingUp,
    CalendarCheck,
    CalendarRange,
    Palmtree,
    CheckCircle2,
    Clock,
    Download,
    FileSpreadsheet,
    Stethoscope,
    Wallet,
    Receipt,
    AlertTriangle,
    PlaneTakeoff,
    X,
    Search,
    ArrowLeft,
    UserCheck,
    UserX,
    Timer,
    Gauge,
    Landmark,
    FileText,
    HandCoins,
    FileDown,
    ArrowRight,
    BarChart2,
    TrendingDown,
    RefreshCw,
    ChevronUp,
    ChevronDown,
    Activity,
    Star,
    Hash,
    CreditCard,
    DollarSign,
    Banknote,
    ShieldCheck,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    LineChart,
    Line,
    AreaChart,
    Area,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    RadialBarChart,
    RadialBar,
} from "recharts";
import "../DashboardStyles/DashboardAdmin.css";

// ── Charte couleurs ───────────────────────────────────────────────────────────
const COLORS = {
    primary: "#204385",
    secondary: "#6e91c1",
    success: "#315f97",
    warning: "#5f91d2",
    error: "#456eaa",
    info: "#3f72b5",
    grey: "#718096",
    purple: "#7898c4",
    teal: "#4f88a8",
};

const CHART_COLORS = ["#204385", "#6e91c1", "#315ba9", "#5f91d2", "#28579c", "#7898c4", "#4f88a8", "#456eaa"];

// ── Config onglets (sans vue d'ensemble) ──────────────────────────────────────
// ── Mock data statiques enrichis ──────────────────────────────────────────────
const generateRandomDate = (start, end) => new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime())).toISOString().slice(0, 10);
const _today = new Date();
const _startOfYear = new Date(_today.getFullYear(), 0, 1);
const _names = ["Ahmed Bennani","Sara Chraibi","Youssef El Amrani","Fatima Zahra Idrissi","Karim Tazi","Nadia Alaoui","Omar Berrada","Leila Fassi","Mehdi Jouahri","Hajar El Kabbaj","Rachid Bouazza","Zineb Senhaji","Amine Lahlou","Salma Kettani","Hassan Skalli","Meryem Bensouda","Khalid Ouazzani","Imane Rahmouni","Mourad Filali","Asmae El Ouafi"];
const _pick = arr => arr[Math.floor(Math.random()*arr.length)];

const MOCK_LEAVES = Array.from({length:156},(_,i)=>({
    id:i, employeeFullName:_names[i%_names.length], leaveTypeName:_pick(["Congé annuel","Maladie","RTT","Congé sans solde","Maternité","Paternité"]),
    startDate:generateRandomDate(_startOfYear,_today), endDate:generateRandomDate(_startOfYear,_today),
    numberOfDays:Math.floor(Math.random()*15)+1, status:_pick(["APPROVED","APPROVED","APPROVED","APPROVED","PENDING","REJECTED"]),
    createdAt:generateRandomDate(_startOfYear,_today),
}));

const MOCK_AVANCES = Array.from({length:45},(_,i)=>({
    id:i, employeeFullName:_names[i%_names.length], requestType:i%3===0?"PRET":"AVANCE",
    montantDemande:Math.floor(Math.random()*25000)+2000, nombreMensualites:_pick([3,6,9,12,18,24]),
    status:_pick(["APPROVED","APPROVED","PENDING","PENDING","IN_PROGRESS","REJECTED"]),
    createdAt:generateRandomDate(_startOfYear,_today),
}));

const MOCK_FRAIS = Array.from({length:120},(_,i)=>({
    id:i, employeeFullName:_names[i%_names.length], category:_pick(["Déplacements","Restauration","Hébergement","Transport","Fournitures","Télécom"]),
    totalAmount:Math.floor(Math.random()*8000)+200, status:_pick(["APPROVED","APPROVED","APPROVED","PENDING","REJECTED"]),
    expenseDate:generateRandomDate(_startOfYear,_today), createdAt:generateRandomDate(_startOfYear,_today),
}));

const MOCK_MISSIONS = Array.from({length:65},(_,i)=>({
    id:i, employeeFullName:_names[i%_names.length], missionType:_pick(["Formation","Client","Technique","Commercial","Audit"]),
    destination:_pick(["Casablanca","Rabat","Marrakech","Tanger","Paris","Agadir"]),
    status:_pick(["COMPLETED","COMPLETED","IN_PROGRESS","PENDING","APPROVED"]),
    departureDate:generateRandomDate(_startOfYear,_today), createdAt:generateRandomDate(_startOfYear,_today),
}));

const MOCK_ATTESTATIONS = Array.from({length:80},(_,i)=>({
    id:i, employeeFullName:_names[i%_names.length], attestationName:_pick(["Attestation de travail","Attestation de salaire","Attestation de congé","Attestation de stage","Attestation de domiciliation"]),
    status:_pick(["APPROVED","APPROVED","APPROVED","PENDING","REJECTED"]),
    createdAt:generateRandomDate(_startOfYear,_today),
}));

const GENERAL_TABS = [
    { id: "rh",       label: "RH & Effectifs", icon: <Users size={16} /> },
    { id: "pointage", label: "Suivi des présences",       icon: <Clock size={16} /> },
    { id: "offboarding", label: "Offboarding", icon: <UserX size={16} /> },
    { id: "etats",    label: "États",          icon: <FileSpreadsheet size={16} /> },
];

const SERVICE_TABS = [
    { id: "conges",       label: "Congés",           icon: <Palmtree size={16} /> },
    { id: "avances",      label: "Avances & Prêts",  icon: <HandCoins size={16} /> },
    { id: "frais",        label: "Notes de frais",   icon: <Wallet size={16} /> },
    { id: "missions",     label: "Ordres de missions", icon: <PlaneTakeoff size={16} /> },
    { id: "attestations", label: "Attestations",     icon: <FileText size={16} /> },
];

const isServiceTab = (tabId) => SERVICE_TABS.some(t => t.id === tabId);

const STATIC_EXCEL_STATES = [
    { id: "etat-collaborateurs", title: "État des effectifs",           description: "Répartition des effectifs par département.",       color: COLORS.primary },
    { id: "etat-conges",         title: "État des demandes de congés",  description: "Historique et statut des demandes.",               color: COLORS.success },
    { id: "etat-avances",        title: "État des avances et prêts",    description: "Échéancier et suivi.",                             color: COLORS.warning },
    { id: "etat-depenses",       title: "Synthèse des notes de frais",  description: "Remboursements par catégorie.",                    color: COLORS.secondary },
    { id: "etat-missions",       title: "Suivi des ordres de mission",  description: "Déplacements et budgets.",                         color: COLORS.info },
    { id: "etat-pointage",       title: "Rapport de pointage",          description: "Heures travaillées et anomalies.",                 color: COLORS.primary },
    { id: "etat-attestations",   title: "Registre des attestations",    description: "Demandes et délais.",                             color: COLORS.grey },
];

// ── Données statiques RH (effectif) ──────────────────────────────────────────
const EXCEL_REPORT_GROUPS = [
    {
        title: "Effectifs & parcours RH",
        description: "Les données administratives et l'évolution des collaborateurs.",
        reports: [
            { id: "etat-collaborateurs", title: "Registre des collaborateurs", description: "Matricule, poste, entité et informations contractuelles.", color: COLORS.primary },
            { id: "etat-salaires", title: "Ã‰tat des salaires", description: "Rémunérations brutes et nettes actuellement paramétrées.", color: COLORS.teal },
        ],
    },
    {
        title: "Temps, congés & présences",
        description: "Le suivi des absences, compteurs et temps de travail.",
        reports: [
            { id: "etat-conges", title: "Demandes de congés", description: "Historique, périodes et statut des demandes.", color: COLORS.success },
            { id: "etat-soldes-conges", title: "Soldes de congés", description: "Compteurs acquis, consommés et restants par collaborateur.", color: COLORS.teal },
            { id: "etat-mouvements-conges", title: "Mouvements de congés", description: "Crédits, débits et ajustements des compteurs.", color: COLORS.info },
            { id: "etat-pointage", title: "Rapport de pointage", description: "Présences, absences, heures et anomalies.", color: COLORS.primary },
        ],
    },
    {
        title: "Demandes & frais",
        description: "Les demandes traitées par les équipes RH et administratives.",
        reports: [
            { id: "etat-avances", title: "Avances et prêts", description: "Montants, échéances et statut des demandes.", color: COLORS.warning },
            { id: "etat-depenses", title: "Notes de frais", description: "Dépenses, catégories et remboursements.", color: COLORS.secondary },
            { id: "etat-missions", title: "Ordres de mission", description: "Déplacements, coûts et suivi des missions.", color: COLORS.info },
            { id: "etat-attestations", title: "Registre des attestations", description: "Demandes d'attestations et leurs statuts.", color: COLORS.grey },
        ],
    },
    {
        title: "Départs & développement",
        description: "Le pilotage des sorties et du catalogue de formation.",
        reports: [
            { id: "etat-offboarding", title: "Suivi des offboardings", description: "Départs prévus, motifs, progression et étapes restantes.", color: COLORS.error },
            { id: "etat-checklist-offboarding", title: "Checklist des départs", description: "Détail de chaque étape, statut et commentaire saisi.", color: COLORS.warning },
        ],
    },
];

const EXCEL_REPORT_GROUPS_DYNAMIC = [
    { title: "Effectifs et parcours RH", description: "Données administratives et évolution des collaborateurs.", reports: [
        { id: "etat-collaborateurs", title: "Registre des collaborateurs", description: "Matricule, poste, entité et contrat.", color: COLORS.primary },
        { id: "etat-salaires", title: "État des salaires", description: "Rémunérations brutes et nettes configurées.", color: COLORS.teal },
    ]},
    { title: "Temps, congés et présences", description: "Suivi des absences, compteurs et temps de travail.", reports: [
        { id: "etat-conges", title: "Demandes de congés", description: "Historique, périodes et statut des demandes.", color: COLORS.success },
        { id: "etat-soldes-conges", title: "Soldes de congés", description: "Compteurs acquis, consommés et restants.", color: COLORS.teal },
        { id: "etat-mouvements-conges", title: "Mouvements de congés", description: "Crédits, débits et ajustements des compteurs.", color: COLORS.info },
        { id: "etat-pointage", title: "Rapport de pointage", description: "Présences, absences, heures et anomalies.", color: COLORS.primary },
    ]},
    { title: "Demandes et frais", description: "Demandes traitées par les équipes RH et administratives.", reports: [
        { id: "etat-avances", title: "Avances et prêts", description: "Montants, échéances et statut des demandes.", color: COLORS.warning },
        { id: "etat-depenses", title: "Notes de frais", description: "Dépenses, catégories et remboursements.", color: COLORS.secondary },
        { id: "etat-missions", title: "Ordres de mission", description: "Déplacements, coûts et suivi des missions.", color: COLORS.info },
        { id: "etat-attestations", title: "Registre des attestations", description: "Demandes d'attestations et leurs statuts.", color: COLORS.grey },
    ]},
    { title: "Départs et développement", description: "Pilotage des sorties et du catalogue de formation.", reports: [
        { id: "etat-offboarding", title: "Suivi des offboardings", description: "Départs, motifs, progression et étapes restantes.", color: COLORS.error },
        { id: "etat-checklist-offboarding", title: "Checklist des départs", description: "Détail de chaque étape, statut et commentaire.", color: COLORS.warning },
    ]},
];

const RH_STATIC = {
    totalEmployees: 101,
    departments: [
        { name: "IT",          count: 85 },
        { name: "RH",          count: 32 },
        { name: "Finance",     count: 45 },
        { name: "Commercial",  count: 120 },
        { name: "Marketing",   count: 58 },
        { name: "Production",  count: 95 },
        { name: "Logistique",  count: 52 },
    ],
    employeeStatus: [
        { name: "CDI",       value: 365, percentage: 75 },
        { name: "CDD",       value: 82,  percentage: 17 },
        { name: "Stagiaires",value: 28,  percentage: 6  },
        { name: "Alternants",value: 12,  percentage: 2  },
    ],
    genderData: [
        { name: "Hommes", value: 278 },
        { name: "Femmes", value: 209 },
    ],
    ancienneteData: [
        { range: "< 1 an",  count: 42 },
        { range: "1-3 ans", count: 95 },
        { range: "3-5 ans", count: 118 },
        { range: "5-10 ans",count: 143 },
        { range: "> 10 ans",count: 89 },
    ],
    internalMobility: 8.5,
    absenteeismRate: 4.2,
    avgSalary: 18500,
    turnoverRate: 6.3,
};

// ── Pointage ──────────────────────────────────────────────────────────────────
const POINTAGE_STORAGE_KEY = "sirh.pointage.externalData.v2";
const POINTAGE_CONFIG = {
    defaultStartTime: "08:00",
    defaultDailyHours: 8,
    weekendDays: [0],
    sections: {
        "001 Mensuel":   { startTime: "08:00", workingDays: [1,2,3,4,5,6], dailyHours: 8 },
        "002 Poste fixe":{ startTime: "08:00", workingDays: [1,2,3,4,5],   dailyHours: 8 },
    },
};

const readPointagePayload = () => {
    try { const raw = localStorage.getItem(POINTAGE_STORAGE_KEY); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
};
const normalizePointageHeader = (v) =>
    String(v || "").trim().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[°&]/g,"").replace(/\s+/g,"").toLowerCase();
const parsePointageDateToISO = (value) => {
    if (!value) return "";
    if (value instanceof Date && !Number.isNaN(value.getTime()))
        return `${value.getFullYear()}-${String(value.getMonth()+1).padStart(2,"0")}-${String(value.getDate()).padStart(2,"0")}`;
    if (typeof value === "number") {
        const p = XLSX.SSF.parse_date_code(value);
        if (p?.y && p?.m && p?.d) return `${p.y}-${String(p.m).padStart(2,"0")}-${String(p.d).padStart(2,"0")}`;
    }
    const text = String(value).trim();
    const match = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (match) {
        const [,day,month,yr] = match;
        const year = Number(yr.length === 2 ? `20${yr}` : yr);
        return `${year}-${String(Number(month)).padStart(2,"0")}-${String(Number(day)).padStart(2,"0")}`;
    }
    const d = new Date(text);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0,10);
};
const normalizePointageTime = (value) => {
    if (!value) return "";
    if (value instanceof Date && !Number.isNaN(value.getTime()))
        return `${String(value.getHours()).padStart(2,"0")}:${String(value.getMinutes()).padStart(2,"0")}`;
    if (typeof value === "number" && value > 0 && value < 1) {
        const tot = Math.round(value * 24 * 60);
        return `${String(Math.floor(tot/60)).padStart(2,"0")}:${String(tot%60).padStart(2,"0")}`;
    }
    const m = String(value).trim().match(/(\d{1,2})[:hH](\d{2})/);
    return m ? `${String(Number(m[1])).padStart(2,"0")}:${m[2]}` : "";
};
const parsePointageFile = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const wb = XLSX.read(ev.target.result, { type:"array", cellDates: false });
                const sheetName = wb.SheetNames.find(n => n.toLowerCase().includes("pointage")) || wb.SheetNames[0];
                const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName],{header:1,defval:"",raw:true});
                const headerIndex = rows.findIndex(r =>
                    r.some(c => normalizePointageHeader(c)==="matricule") &&
                    r.some(c => normalizePointageHeader(c).includes("nomprenom"))
                );
                if (headerIndex < 0) throw new Error("En-têtes Matricule / Nom & Prénom introuvables.");
                const headers = rows[headerIndex].map(normalizePointageHeader);
                const find = (...names) => headers.findIndex(h => names.includes(h));
                const matriculeIndex = find("matricule");
                const nameIndex = headers.findIndex(h => h.includes("nomprenom"));
                const cardIndex = headers.findIndex(h => h.includes("ncarte")||h.includes("nocarte"));
                const sectionIndex = find("section");
                const dateIndex = find("date");
                const dayIndex = find("jour");
                const punchIndexes = headers.map((h,i) => (/^p\d{1,2}$/.test(h) ? i : -1)).filter(i=>i>=0);
                const parsedRows = rows.slice(headerIndex+1)
                    .map(r => ({
                        matricule: String(r[matriculeIndex]||"").trim(),
                        nomPrenom: String(r[nameIndex]||"").trim(),
                        numeroCarte: String(r[cardIndex]||"").trim(),
                        section: String(r[sectionIndex]||"").trim(),
                        date: parsePointageDateToISO(r[dateIndex]),
                        jour: String(r[dayIndex]||"").trim().toLowerCase(),
                        pointages: punchIndexes.map(i=>normalizePointageTime(r[i])).filter(Boolean),
                    }))
                    .filter(r => r.matricule && r.nomPrenom && r.date && r.matricule.toLowerCase()!=="matricule");
                if (!parsedRows.length) throw new Error("Aucune ligne de pointage exploitable.");
                const existing = readPointagePayload();
                const existingRows = existing?.rows || [];
                const rowMap = new Map();
                existingRows.forEach(r => rowMap.set(`${r.matricule}_${r.date}`,r));
                parsedRows.forEach(r => rowMap.set(`${r.matricule}_${r.date}`,r));
                const mergedRows = Array.from(rowMap.values());
                const fileNames = new Set(existing?.fileNames || []);
                if (existing?.fileName) fileNames.add(existing.fileName);
                fileNames.add(file.name);
                const payload = { fileNames: Array.from(fileNames), importedAt: new Date().toISOString(), sheetName, rows: mergedRows };
                localStorage.setItem(POINTAGE_STORAGE_KEY, JSON.stringify(payload));
                window.dispatchEvent(new CustomEvent("sirh-pointage-imported",{detail:payload}));
                resolve(payload);
            } catch(err) { reject(err); }
        };
        reader.onerror = () => reject(new Error("Lecture impossible."));
        reader.readAsArrayBuffer(file);
    });
const timeToMinutes = (t) => {
    const m = String(t||"").match(/^(\d{1,2}):(\d{2})/);
    return m ? Number(m[1])*60+Number(m[2]) : null;
};
const minutesToTime = (min) => {
    if (min===null||Number.isNaN(min)) return "—";
    return `${String(Math.floor(min/60)).padStart(2,"0")}:${String(Math.round(min%60)).padStart(2,"0")}`;
};
const formatHours = (h) => `${h.toFixed(1).replace(".",",")}h`;
const getPointageSectionConfig = (section="") => {
    const exact = POINTAGE_CONFIG.sections[section];
    if (exact) return exact;
    const m = Object.entries(POINTAGE_CONFIG.sections).find(([k])=>section.toLowerCase().includes(k.toLowerCase()));
    return m?.[1] || {};
};
const isExpectedWorkday = (dateISO, section) => {
    const d = new Date(`${dateISO}T00:00:00`);
    const dow = d.getDay();
    const cfg = getPointageSectionConfig(section);
    const workDays = cfg.workingDays || [1,2,3,4,5];
    return workDays.includes(dow);
};
const getPointageStatus = (row) => {
    if (!row?.pointages?.length) return "absence";
    if (row.pointages.length === 1) return "anomalie";
    const times = row.pointages.map(timeToMinutes).filter(t => t !== null);
    if (times.length < 2) return "anomalie";
    const first = times[0];
    const last = times[times.length - 1];
    if (first === last) return "anomalie";
    return "present";
};
const buildMonthDays = (period) => {
    if (!period) return [];
    const [y, m] = period.split("-").map(Number);
    const days = new Date(y, m, 0).getDate();
    return Array.from({length: days}, (_,i) => `${period}-${String(i+1).padStart(2,"0")}`);
};
const calculatePointageAnalytics = (payload, section, period, search, dateDebut, dateFin) => {
    const empty = { rows:[], sections:[], periods:[], employees:[], allEmployees:[], trend:[], daily:[], sectionsData:[], topAbsences:[], topAnomalies:[], kpis:{effectif:0,heures_moy:0,presence_rate:0,anomalies_pct:0,anomalies_tot:0,heures_totales:0}, globalPresenceRate:0, absenceTotal:0, overtimeTotal:0 };
    if (!payload?.rows?.length) return empty;
    let rows = payload.rows;
    if (dateDebut) rows = rows.filter(r => r.date >= dateDebut);
    if (dateFin)   rows = rows.filter(r => r.date <= dateFin);
    const sections = [...new Set(rows.map(r=>r.section))].sort();
    const periods  = [...new Set(rows.map(r=>r.date.slice(0,7)))].sort().reverse();
    const filteredRows = rows.filter(r => {
        if (section !== "all" && r.section !== section) return false;
        if (period && !r.date.startsWith(period)) return false;
        return true;
    });
    const empMap = new Map();
    filteredRows.forEach(r => {
        if (!empMap.has(r.matricule)) empMap.set(r.matricule,{ matricule:r.matricule, nomPrenom:r.nomPrenom, section:r.section, byDate:new Map() });
        empMap.get(r.matricule).byDate.set(r.date,r);
    });
    const monthDays = buildMonthDays(period);
    let expectedTotal=0, absenceTotal=0, anomalyTotal=0, overtimeTotal=0;
    const searchedEmployees = [];
    const employeeList = [];
    for (const emp of empMap.values()) {
        let worked=0,absent=0,anomaly=0,totalHours=0,overtime=0;
        const cfg = getPointageSectionConfig(emp.section);
        const expectedDays = monthDays.filter(d=>isExpectedWorkday(d,emp.section)).length;
        for (const d of monthDays) {
            if (!isExpectedWorkday(d,emp.section)) continue;
            expectedTotal++;
            const row = emp.byDate.get(d);
            if (!row?.pointages?.length) { absent++; absenceTotal++; continue; }
            worked++;
            const times = row.pointages.map(timeToMinutes).filter(t=>t!==null);
            if (times.length >= 2) {
                const amp = times[times.length-1]-times[0];
                const daily = cfg.dailyHours || POINTAGE_CONFIG.defaultDailyHours;
                totalHours += amp/60;
                if (amp/60 > daily+0.5) { overtime++; overtimeTotal++; }
            }
            const status = getPointageStatus(row);
            if (status==="anomalie") { anomaly++; anomalyTotal++; }
        }
        const presenceRate = expectedDays ? Math.round((worked/expectedDays)*100) : 0;
        const avgAmp = worked ? totalHours/worked : 0;
        const absences = expectedDays-worked;
        const entry = { ...emp, workedDays:worked, absences, expectedDays, totalHours, overtimeHours:overtime, anomalyCount:anomaly, presenceRate, averageAmplitude:avgAmp };
        employeeList.push(entry);
        const searchMatch = !search || emp.nomPrenom.toLowerCase().includes(search.toLowerCase()) || emp.matricule.toLowerCase().includes(search.toLowerCase());
        if (searchMatch) searchedEmployees.push(entry);
    }
    const daily = monthDays.map(d => {
        const dayRows = filteredRows.filter(r=>r.date===d&&r.pointages?.length>0);
        const hMoy = dayRows.length ? dayRows.reduce((s,r)=>{
            const t = r.pointages.map(timeToMinutes).filter(x=>x!==null);
            return t.length>=2 ? s+(t[t.length-1]-t[0])/60 : s;
        },0)/dayRows.length : 0;
        return { jour:d.slice(8), effectif:dayRows.length, heures:Math.round(hMoy*10)/10 };
    });
    const sectionsData = sections.map(s => ({
        nom:s,
        effectif:employeeList.filter(e=>e.section===s).length,
        anomalies:employeeList.filter(e=>e.section===s).reduce((sum,e)=>sum+e.anomalyCount,0),
    }));
    const topAbsences = [...employeeList].filter(e=>e.absences>0).sort((a,b)=>b.absences-a.absences).slice(0,8)
        .map(e=>({ matricule:e.matricule, nom:e.nomPrenom, section:e.section, jours:e.workedDays, absences:e.absences }));
    const topAnomalies = [...employeeList].filter(e=>e.anomalyCount>0).sort((a,b)=>b.anomalyCount-a.anomalyCount).slice(0,8)
        .map(e=>({ matricule:e.matricule, nom:e.nomPrenom, section:e.section, anomalies:e.anomalyCount }));
    const totalHoursPointées = employeeList.reduce((s,e)=>s+e.totalHours,0);
    const totalPresentDays = employeeList.reduce((s,e)=>s+e.workedDays,0);
    const globalPresenceRate = expectedTotal ? Math.round(((expectedTotal-absenceTotal)/expectedTotal)*100) : 0;
    const kpis = {
        effectif: employeeList.length,
        heures_moy: totalPresentDays ? Math.round((totalHoursPointées/totalPresentDays)*10)/10 : 0,
        presence_rate: globalPresenceRate,
        anomalies_pct: totalPresentDays ? Math.round((anomalyTotal/totalPresentDays)*1000)/10 : 0,
        anomalies_tot: anomalyTotal,
        heures_totales: Math.round(totalHoursPointées),
    };
    return { rows:filteredRows, sections, periods, employees:searchedEmployees, allEmployees:employeeList, trend:[], daily, sectionsData, topAbsences, topAnomalies, kpis, globalPresenceRate, absenceTotal, overtimeTotal };
};

// ── Filtre date ───────────────────────────────────────────────────────────────
const isWithinDateRange = (dateStr, start, end) => {
    if (!start && !end) return true;
    if (!dateStr) return false;
    const dStr = String(dateStr).slice(0, 10);
    if (start && dStr < start) return false;
    if (end && dStr > end) return false;
    return true;
};

// ── Helpers format ─────────────────────────────────────────────────────────────
const fmtK = (n) => n >= 1000 ? `${(n/1000).toFixed(1)}K` : String(Math.round(n));
const fmtMAD = (n) => `${fmtK(n)} MAD`;

// Résoudre le champ date d'un objet (multi-clé)
const getDate = (r) => r?.createdAt || r?.dateDemande || r?.dateDepense || r?.ordreMissionDateDebut || r?.date || r?.dateDebut || r?.requestDate || "";

// ── Sous-composants ───────────────────────────────────────────────────────────
function StatCard({ title, value, subtitle, icon, color, trend, onClick }) {
    const { t } = useTranslation();
    const trendIsPlus = typeof trend === "string" && trend.trim().startsWith("+");
    return (
        <div className="daStatCard" onClick={onClick || (() => window.dispatchEvent(new CustomEvent("dashboard-card-click", { detail: { title } })))} style={{ cursor: "pointer", transition: "transform 0.15s ease, box-shadow 0.15s ease" }}>
            <div className="daStatCard__row">
                <div className="daStatCard__left">
                    <div className="daStatCard__title">{t(title)}</div>
                    <div className="daStatCard__value" style={{ color: color || COLORS.primary }}>{value}</div>
                    {subtitle ? <div className="daStatCard__sub">{t(subtitle)}</div> : null}
                    {trend ? <span className={`daTrend ${trendIsPlus ? "is-plus" : "is-minus"}`}>{trend}</span> : null}
                </div>
                <div className="daStatIcon" style={{ color: color||COLORS.primary, background:`${color||COLORS.primary}15`, borderColor:`${color||COLORS.primary}25` }}>
                    {icon}
                </div>
            </div>
        </div>
    );
}

function LeaveCardDetailModal({ modalData, onClose }) {
    const { t } = useTranslation();
    if (!modalData) return null;
    const { title, type, rows, extra } = modalData;

    return (
        <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20
        }} onClick={onClose}>
            <div style={{
                background: "#fff", borderRadius: 16, width: "100%", maxWidth: 850,
                maxHeight: "85vh", display: "flex", flexDirection: "column",
                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
                overflow: "hidden"
            }} onClick={e => e.stopPropagation()}>
                <div style={{
                    padding: "16px 24px", borderBottom: "1px solid #e5e7eb",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: "#f8fafc"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${COLORS.primary}15`, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.primary }}>
                            <Palmtree size={20} />
                        </div>
                        <div>
                            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", margin: 0 }}>{t(title)}</h3>
                            <span style={{ fontSize: 12, color: "#64748b" }}>
                                {type === "soldeMoyen" ? t("{{count}} collaborateur(s)", { count: extra?.employeeBalancesList?.length || 0 }) : t("{{count}} demande(s)", { count: rows?.length || 0 })}
                            </span>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: "#f1f5f9", border: "none", cursor: "pointer", color: "#64748b", padding: 6, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <X size={18} />
                    </button>
                </div>

                <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
                    {type === "soldeMoyen" ? (
                        <MiniTable
                            columns={[
                                { key: "name", label: t("Collaborateur"), render: (v) => <strong>{v || "–"}</strong> },
                                { key: "soldeRestant", label: t("Solde restant (congé annuel)"), render: (v) => <strong style={{ color: COLORS.teal, fontSize: 14 }}>{v !== undefined && v !== null ? t("{{count}} jour(s)", { count: v }) : "–"}</strong> },
                            ]}
                            rows={extra?.employeeBalancesList || []}
                            emptyMsg={t("Aucun solde trouvé pour les collaborateurs")}
                        />
                    ) : type === "typeCount" ? (
                        <MiniTable
                            columns={[
                                { key: "name", label: t("Type de congé"), render: (v) => <strong>{t(v)}</strong> },
                                { key: "value", label: t("Nombre de demandes"), render: (v) => <span style={{ fontWeight: 600, color: COLORS.primary }}>{v}</span> },
                                { key: "percentage", label: t("Part des demandes"), render: (_, r) => `${Math.round((r.value / (extra?.totalLeaves || 1)) * 100)}%` },
                            ]}
                            rows={extra?.typeData || []}
                            emptyMsg={t("Aucun type de congé trouvé")}
                        />
                    ) : (
                        <MiniTable
                            columns={[
                                { key: "employeeFullName", label: t("Collaborateur"), render: (v) => <strong>{v || "–"}</strong> },
                                { key: "leaveTypeName", label: t("Type"), render: (v) => v ? t(v) : "–" },
                                { key: "startDate", label: t("Début"), render: (v, r) => r.startDate || r.dateDebut || "–" },
                                { key: "endDate", label: t("Fin"), render: (v, r) => r.endDate || r.dateFin || "–" },
                                { key: "numberOfDays", label: t("Durée"), render: (v) => <span>{v !== undefined ? t("{{count}} j", { count: v }) : "–"}</span> },
                                { key: "status", label: t("Statut"), render: (v) => <StatusBadge statut={v} /> },
                            ]}
                            rows={rows || []}
                            emptyMsg={t("Aucune demande correspondante")}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function CardDetailModal({ modalData, onClose }) {
    const { t } = useTranslation();
    if (!modalData) return null;
    const { title, rows = [], columns = [], emptyMsg = "Aucune donnée correspondante" } = modalData;
    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
            <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 900, maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
                <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc" }}>
                    <div><h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", margin: 0 }}>{t(title)}</h3><span style={{ fontSize: 12, color: "#64748b" }}>{t("{{count}} élément(s)", { count: rows.length })}</span></div>
                    <button type="button" onClick={onClose} style={{ background: "#f1f5f9", border: "none", cursor: "pointer", color: "#64748b", padding: 6, borderRadius: 8, display: "flex" }}><X size={18} /></button>
                </div>
                <div style={{ padding: 20, overflowY: "auto", flex: 1 }}><MiniTable columns={columns} rows={rows} emptyMsg={emptyMsg} /></div>
            </div>
        </div>
    );
}

function StaticDataBadge() {
    const { t } = useTranslation();
    return (
        <p style={{
            fontSize:12, fontWeight:600, color:"#92400e",
            background:"#fef3c7", border:"1px solid #fde68a",
            borderRadius:8, padding:"6px 12px",
            display:"inline-flex", alignItems:"center", gap:6,
            marginBottom:14
        }}>
            <AlertTriangle size={13} /> {t("Données statiques de démonstration")}
        </p>
    );
}

function ChartCard({ title, children, fullWidth }) {
    const { t } = useTranslation();
    return (
        <div className="daChartCard" style={fullWidth ? { gridColumn:"1/-1" } : {}}>
            <div className="daChartCard__title">{t(title)}</div>
            <div className="daChartCard__body">{children}</div>
        </div>
    );
}

function SectionTitle({ icon, children }) {
    const { t } = useTranslation();
    return (
        <div className="daSectionTitle">
            <span className="daSectionTitle__icon">{icon}</span>
            <span>{typeof children === "string" ? t(children) : children}</span>
        </div>
    );
}

function MiniTable({ columns, rows, emptyMsg = "Aucune donnée" }) {
    const { t, i18n } = useTranslation();
    if (!rows?.length) return <span style={{ fontSize:13, color:"#9ca3af" }}>{t(emptyMsg)}</span>;
    return (
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
                <tr>
                    {columns.map(c => (
                        <th key={c.key} style={{ textAlign: i18n.dir() === "rtl" ? "right" : "left", padding:"6px 10px", fontWeight:600, color:"#6b7280", fontSize:12, borderBottom:"1px solid #f3f4f6" }}>
                            {t(c.label)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={i} style={{ borderBottom:"1px solid #f9fafb" }}>
                        {columns.map(c => (
                            <td key={c.key} style={{ padding:"7px 10px", color:"#374151" }}>
                                {c.render ? c.render(row[c.key], row) : row[c.key]}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function LoadingOverlay() {
    const { t } = useTranslation();
    return (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:60, color:"#9ca3af", gap:10 }}>
            <RefreshCw size={20} style={{ animation:"spin 1s linear infinite" }} />
            <span>{t("Chargement des données…")}</span>
        </div>
    );
}

function DateFilterFields({ dateDebut, dateFin, onChangeDebut, onChangeFin }) {
    const { t } = useTranslation();
    const handleReset = () => {
        onChangeDebut("");
        onChangeFin("");
    };

    return (
        <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"8px 14px", marginBottom:18, boxShadow:"0 1px 2px rgba(0,0,0,0.03)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, fontWeight:600, color:"#374151" }}>
                <CalendarRange size={16} color={COLORS.primary} />
                <span>{t("Période")} :</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <label htmlFor="da-date-debut" style={{ fontSize:12.5, fontWeight:500, color:"#4b5563" }}>{t("Du")}</label>
                <input id="da-date-debut" type="date" value={dateDebut} onChange={e=>onChangeDebut(e.target.value)}
                    style={{ border:"1px solid #d1d5db", borderRadius:8, padding:"5px 10px", fontSize:13, color:"#1f2937", outline:"none" }} />
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <label htmlFor="da-date-fin" style={{ fontSize:12.5, fontWeight:500, color:"#4b5563" }}>{t("Au")}</label>
                <input id="da-date-fin" type="date" value={dateFin} onChange={e=>onChangeFin(e.target.value)}
                    style={{ border:"1px solid #d1d5db", borderRadius:8, padding:"5px 10px", fontSize:13, color:"#1f2937", outline:"none" }} />
            </div>
            {(dateDebut || dateFin) && (
                <button type="button" onClick={handleReset}
                    style={{ background:"#edf4fc", border:"1px solid #c5d8f1", borderRadius:8, padding:"5px 12px", fontSize:12.5, fontWeight:600, color:"#315f97", cursor:"pointer", display:"flex", alignItems:"center", gap:5, transition:"all 0.15s ease" }}>
                    <RefreshCw size={13} /> {t("Réinitialiser")}
                </button>
            )}
        </div>
    );
}

function ExcelStateCard({ title, description, color, onDownload }) {
    const { t } = useTranslation();
    return (
        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:18, display:"flex", flexDirection:"column", gap:12, boxShadow:"0 1px 2px rgba(16,24,40,0.04)" }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                <div style={{ width:40, height:40, minWidth:40, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", color, background:`${color}15`, border:`1px solid ${color}25` }}>
                    <FileSpreadsheet size={20} />
                </div>
                <div>
                    <div style={{ fontSize:15, fontWeight:600, color:"#1f2937" }}>{t(title)}</div>
                    <div style={{ fontSize:12.5, color:"#6b7280", marginTop:4, lineHeight:1.4 }}>{t(description)}</div>
                </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", paddingTop:8, borderTop:"1px dashed #eef0f2" }}>
                <span style={{ fontSize:11.5, color:"#9ca3af", display:"flex", alignItems:"center", gap:5 }}>
                    <Clock size={12} /> {t("Mis à jour à la demande")}
                </span>
                <button type="button" onClick={onDownload}
                    style={{ display:"flex", alignItems:"center", gap:6, border:"none", borderRadius:8, padding:"7px 12px", fontSize:12.5, fontWeight:600, color:"#fff", background:color, cursor:"pointer" }}>
                    <Download size={14} /> {t("Télécharger")}
                </button>
            </div>
        </div>
    );
}

const STATUS_STYLES = {
    "APPROVED":            { label:"Approuvé",                 bg:"#e8f1fb", color:"#24558f" },
    "APPROVED_BY_MANAGER": { label:"Approuvé Manager",         bg:"#e8f1fb", color:"#24558f" },
    "APPROVED_RH":         { label:"Approuvé RH",              bg:"#e8f1fb", color:"#24558f" },
    "PENDING":             { label:"En attente",               bg:"#edf4fc", color:"#315f97" },
    "PENDING_MANAGER":     { label:"En attente du manager",    bg:"#edf4fc", color:"#315f97" },
    "PENDING_ADMIN":       { label:"En attente de l'admin RH", bg:"#edf4fc", color:"#315f97" },
    "PENDING_RH":          { label:"En attente RH",            bg:"#edf4fc", color:"#315f97" },
    "REJECTED":            { label:"Refusé",                   bg:"#e7eef8", color:"#345a8d" },
    "REJECTED_MANAGER":    { label:"Refusé Manager",           bg:"#e7eef8", color:"#345a8d" },
    "REJECTED_ADMIN":      { label:"Refusé Admin/RH",          bg:"#e7eef8", color:"#345a8d" },
    "IN_PROGRESS":         { label:"En cours",                 bg:"#eaf2fc", color:"#315f97" },
    "COMPLETED":           { label:"Terminée",                 bg:"#e8f1fb", color:"#24558f" },
    "CANCELLED":           { label:"Annulé",                   bg:"#f0f4f9", color:"#64748b" },
    "DELIVERED":           { label:"Délivrée",                 bg:"#e8f1fb", color:"#24558f" },
};

const getStatusStyle = (statut) => {
    const s = statut?.toUpperCase?.() || statut || "";
    let style = STATUS_STYLES[s];
    if (!style) {
        if (s.includes("PENDING")) style = STATUS_STYLES.PENDING;
        else if (s.includes("APPROVED")) style = STATUS_STYLES.APPROVED;
        else if (s.includes("REJECT")) style = STATUS_STYLES.REJECTED;
        else style = { label: statut || "–", bg:"#f0f4f9", color:"#64748b" };
    }
    return style;
};

const getStatusLabel = (statut) => i18n.t(getStatusStyle(statut).label);

function StatusBadge({ statut }) {
    const { t } = useTranslation();
    const style = getStatusStyle(statut);
    return (
        <span style={{ padding:"3px 9px", borderRadius:999, fontSize:12, fontWeight:600, background:style.bg, color:style.color, display:"inline-block" }}>
            {t(style.label)}
        </span>
    );
}

// ── Composant principal ───────────────────────────────────────────────────────
const expenseCategories = (item) => {
    const categories = [...new Set((item?.items || []).map(line => line.category).filter(Boolean))];
    return categories.length ? categories.join(", ") : item?.category || item?.categorie || "Autre";
};

export default function DashboardAdmin() {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    // ── Redux states ─────────────────────────────────────────────────────────
    const leaveRequestsState = useSelector((state) => state.leaveRequests || {});
    const avanceRequestsState = useSelector((state) => state.avanceRequests || {});
    const noteDeFraisRequestsState = useSelector((state) => state.noteDeFraisRequests || {});
    const missionRequestsState = useSelector((state) => state.missionRequests || {});
    const attestationsState = useSelector((state) => state.attestations || {});
    const offboardingState = useSelector((state) => state.offboarding || {});
    const usersState = useSelector((state) => state.user || {});
    const entitesState = useSelector((state) => state.entites || {});
    const offboardings = offboardingState.offboardings || [];
    const etapesByOffboarding = offboardingState.etapesByOffboarding || {};
    const users = usersState.users || [];
    const entites = entitesState.entites || [];
    const [dashboardFrais, setDashboardFrais] = useState([]);
    const [dashboardAvances, setDashboardAvances] = useState([]);
    const [reportData, setReportData] = useState({
        salaires: [], parcours: [], avantages: [], formations: [], soldesConges: [], mouvementsConges: [],
    });

    // Combined lists from Redux store:
    const realLeaves = useMemo(() => {
        const inbox = leaveRequestsState.adminInbox || [];
        const history = leaveRequestsState.allRequests || [];
        const combined = [...inbox, ...history];
        const seen = new Set();
        return combined.filter(item => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
        });
    }, [leaveRequestsState.adminInbox, leaveRequestsState.allRequests]);

    const realAvances = useMemo(() => {
        const inbox = avanceRequestsState.adminInbox || [];
        const history = avanceRequestsState.adminHistory || [];
        const combined = [...inbox, ...history, ...dashboardAvances];
        const seen = new Set();
        return combined.filter(item => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
        });
    }, [avanceRequestsState.adminInbox, avanceRequestsState.adminHistory, dashboardAvances]);

    const realFrais = useMemo(() => {
        const inbox = noteDeFraisRequestsState.adminInbox || [];
        const history = noteDeFraisRequestsState.adminHistory || [];
        const combined = [...inbox, ...history, ...dashboardFrais];
        const seen = new Set();
        return combined.filter(item => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
        });
    }, [noteDeFraisRequestsState.adminInbox, noteDeFraisRequestsState.adminHistory, dashboardFrais]);

    const realMissions = useMemo(() => {
        const inbox = missionRequestsState.adminInbox || [];
        const history = missionRequestsState.adminHistory || [];
        // Le tableau de bord est une vue de suivi : inclure également les
        // demandes encore chez le manager (PENDING_MANAGER), absentes de l'inbox RH.
        const allRequests = missionRequestsState.allRequests || [];
        const combined = [...inbox, ...history, ...allRequests];
        const seen = new Set();
        return combined.filter(item => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
        });
    }, [missionRequestsState.adminInbox, missionRequestsState.adminHistory, missionRequestsState.allRequests]);

    const realAttestations = useMemo(() => {
        const seen = new Set();
        return [...(attestationsState.adminInbox || []), ...(attestationsState.adminHistory || [])]
            .filter((item) => !seen.has(item.id) && seen.add(item.id));
    }, [attestationsState.adminInbox, attestationsState.adminHistory]);

    // Dispatch loading actions on mount
    useEffect(() => {
        dispatch(getAdminInboxRequest());
        dispatch(getAllLeaveRequestsRequest());

        dispatch(getAdminAvanceInboxRequest());
        dispatch(getAdminAvanceHistoryRequest());

        dispatch(getAdminNoteDeFraisInboxRequest());
        dispatch(getAdminNoteDeFraisHistoryRequest());

        dispatch(getAdminMissionInboxRequest());
        dispatch(getAdminMissionHistoryRequest());
        dispatch(getAllMissionRequestsRequest());

        dispatch(getAdminAttestationInboxRequest());
        dispatch(getAdminAttestationHistoryRequest());
        dispatch(fetchOffboardingsRequest());
        dispatch(getAllUsersRequest());
        dispatch(getAllEntitesRequest());
    }, [dispatch]);

    useEffect(() => {
        offboardings.forEach((offboarding) => {
            if (offboarding?.id && !etapesByOffboarding[String(offboarding.id)]) {
                dispatch(fetchEtapesRequest(offboarding.id));
            }
        });
    }, [dispatch, offboardings, etapesByOffboarding]);

    useEffect(() => {
        const token = sessionStorage.getItem("token");
        if (!token) return;

        Promise.all([
            getRequest({ url: `${API_BASE_URL}/api/note-frais-requests/admin/all`, token }),
            getRequest({ url: `${API_BASE_URL}/api/avance-requests/admin/all`, token }),
        ])
            .then(([fraisResponse, avancesResponse]) => {
                setDashboardFrais(fraisResponse.data || []);
                setDashboardAvances(avancesResponse.data || []);
            })
            .catch(() => {
                setDashboardFrais([]);
                setDashboardAvances([]);
            });
    }, []);

    // Ces listes sont chargées exclusivement pour les exports RH : elles ne
    // modifient pas les autres onglets du dashboard.
    useEffect(() => {
        const token = sessionStorage.getItem("token");
        if (!token) return;

        Promise.all([
            getRequest({ url: `${API_BASE_URL}/api/salaires/current`, token }),
            getRequest({ url: `${API_BASE_URL}/api/employee-career-paths`, token }),
            getRequest({ url: `${API_BASE_URL}/api/paramavantages/avantages`, token }),
            getRequest({ url: `${API_BASE_URL}/api/formations`, token }),
            getRequest({ url: `${API_BASE_URL}/api/soldes-conges`, token }),
            getRequest({ url: `${API_BASE_URL}/api/soldes-conges/history/all`, token }),
        ]).then(([salaires, parcours, avantages, formations, soldesConges, mouvementsConges]) => {
            setReportData({
                salaires: salaires.data || [], parcours: parcours.data || [], avantages: avantages.data || [],
                formations: formations.data || [], soldesConges: soldesConges.data || [], mouvementsConges: mouvementsConges.data || [],
            });
        }).catch(() => setReportData({ salaires: [], parcours: [], avantages: [], formations: [], soldesConges: [], mouvementsConges: [] }));
    }, []);

    // ── Local state ───────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState(SERVICE_TABS[0].id);
    const [dateDebut, setDateDebut] = useState("");
    const [dateFin,   setDateFin]   = useState("");
    const [selectedCongesCardModal, setSelectedCongesCardModal] = useState(null);
    const [selectedCardModal, setSelectedCardModal] = useState(null);

    const openCardDetails = (title, rows, columns, emptyMsg) =>
        setSelectedCardModal({ title, rows, columns, emptyMsg });

    const [pointageDayDetail, setPointageDayDetail] = useState(null); // { date, row, employee }
    const [detailRangeStart, setDetailRangeStart] = useState("");
    const [detailRangeEnd, setDetailRangeEnd] = useState("");

    // Pointage
    const [pointagePayload, setPointagePayload]     = useState(() => readPointagePayload());
    const [pointageSection, setPointageSection]     = useState("all");
    const [pointagePeriod,  setPointagePeriod]      = useState("");
    const [pointageSearch,  setPointageSearch]      = useState("");
    const [selectedPointageEmployee, setSelectedPointageEmployee] = useState(null);
    const [pointageImporting,  setPointageImporting]  = useState(false);
    const [pointageImportError,setPointageImportError] = useState("");

    const lastUpdated = useMemo(() => new Date().toLocaleString(i18n.language), [i18n.language]);

    // ── Données réelles filtrées par date ─────────────────────────────────────
    const filteredLeaves      = useMemo(() => realLeaves.filter(r       => isWithinDateRange(getDate(r), dateDebut, dateFin)), [realLeaves, dateDebut, dateFin]);
    const filteredAvances     = useMemo(() => realAvances.filter(r      => isWithinDateRange(getDate(r), dateDebut, dateFin)), [realAvances, dateDebut, dateFin]);
    const filteredFrais       = useMemo(() => realFrais.filter(r        => isWithinDateRange(getDate(r), dateDebut, dateFin)), [realFrais, dateDebut, dateFin]);
    const filteredMissions    = useMemo(() => realMissions.filter(r     => isWithinDateRange(getDate(r), dateDebut, dateFin)), [realMissions, dateDebut, dateFin]);
    const filteredAttestations= useMemo(() => realAttestations.filter(r => isWithinDateRange(getDate(r), dateDebut, dateFin)), [realAttestations, dateDebut, dateFin]);

    // Pointage analytics
    const pointageAnalytics = useMemo(
        () => calculatePointageAnalytics(pointagePayload, pointageSection, pointagePeriod || pointagePayload?.rows?.[0]?.date?.slice(0,7)||"", pointageSearch, dateDebut, dateFin),
        [pointagePayload, pointageSection, pointagePeriod, pointageSearch, dateDebut, dateFin]
    );

    useEffect(() => {
        const handleCardClick = (event) => {
            const commonColumns = [
                { key: "employeeFullName", label: "Collaborateur", render: (v, row) => v || row.employeeName || row.collaborateur || "–" },
                { key: "requestType", label: "Type", render: (v, row) => row.items?.length ? expenseCategories(row) : v || row.missionType || row.attestationName || row.category || row.type || "–" },
                { key: "amount", label: "Montant", render: (_, row) => (row.montantDemande || row.totalAmount || row.montant) != null ? Number(row.montantDemande || row.totalAmount || row.montant).toLocaleString("fr-FR") + " MAD" : "–" },
                { key: "date", label: "Date", render: (_, row) => getDate(row) || "–" },
                { key: "status", label: "Statut", render: (v, row) => <StatusBadge statut={v || row.statut} /> },
            ];
            const rowsByTab = { avances: filteredAvances, frais: filteredFrais, missions: filteredMissions, attestations: filteredAttestations, pointage: pointageAnalytics.allEmployees, rh: RH_STATIC.departments };
            const columns = activeTab === "pointage" ? [{ key: "nomPrenom", label: "Collaborateur" }, { key: "matricule", label: "Matricule" }, { key: "workedDays", label: "Jours travaillés" }, { key: "absences", label: "Absences" }, { key: "totalHours", label: "Heures" }] : activeTab === "rh" ? [{ key: "name", label: "Département" }, { key: "count", label: "Effectif" }] : commonColumns;
            const cardTitle = (event.detail.title || "").toLowerCase();
            let rows = rowsByTab[activeTab] || [];
            if (cardTitle.includes("en attente")) rows = rows.filter((row) => (row.status || row.statut || "").toUpperCase().includes("PENDING"));
            else if (cardTitle.includes("approuv") || cardTitle.includes("délivr") || cardTitle.includes("livraison")) rows = rows.filter((row) => { const status = (row.status || row.statut || "").toUpperCase(); return status.includes("APPROVED") || status.includes("DELIVERED"); });
            else if (cardTitle.includes("refus")) rows = rows.filter((row) => (row.status || row.statut || "").toUpperCase().includes("REJECT"));
            else if (cardTitle.includes("en cours")) rows = rows.filter((row) => { const status = (row.status || row.statut || "").toUpperCase(); return status.includes("PROGRESS") || status === "IN_PROGRESS"; });
            else if (cardTitle.includes("termin")) rows = rows.filter((row) => { const status = (row.status || row.statut || "").toUpperCase(); return status.includes("COMPLETED") || status.includes("TERMIN"); });
            openCardDetails(event.detail.title, rows, columns, "Aucune donnée correspondante");
        };
        window.addEventListener("dashboard-card-click", handleCardClick);
        return () => window.removeEventListener("dashboard-card-click", handleCardClick);
    }, [activeTab, filteredAvances, filteredFrais, filteredMissions, filteredAttestations, pointageAnalytics.allEmployees]);

    useEffect(() => {
        if (!pointagePeriod && pointageAnalytics.periods[0]) setPointagePeriod(pointageAnalytics.periods[0]);
    }, [pointageAnalytics.periods, pointagePeriod]);

    useEffect(() => {
        const refresh = (ev) => setPointagePayload(ev?.detail || readPointagePayload());
        window.addEventListener("sirh-pointage-imported", refresh);
        window.addEventListener("storage", refresh);
        return () => { window.removeEventListener("sirh-pointage-imported",refresh); window.removeEventListener("storage",refresh); };
    }, []);

    useEffect(() => {
        getRequest({ url: `${API_BASE_URL}/api/pointage-import`, token: sessionStorage.getItem("token") })
            .then((response) => { if (response.data?.rows) { localStorage.setItem(POINTAGE_STORAGE_KEY, JSON.stringify(response.data)); setPointagePayload(response.data); } })
            .catch(() => {});
    }, []);

    const handlePointageImport = (file) => {
        if (!file) return;
        setPointageImporting(true); setPointageImportError("");
        parsePointageFile(file)
            .then(async (p) => { await putRequest({ url: `${API_BASE_URL}/api/pointage-import`, data: p, token: sessionStorage.getItem("token") }); setPointagePayload(p); setPointagePeriod(p.rows?.[0]?.date?.slice(0,7)||""); setPointageSection("all"); setPointageSearch(""); setSelectedPointageEmployee(null); })
            .catch(e => setPointageImportError(e?.message||"Import impossible."))
            .finally(() => setPointageImporting(false));
    };

    // ── Générateurs Excel ─────────────────────────────────────────────────────
    const downloadExcel = (dataList, sheetName, filename) => {
        const ws = XLSX.utils.json_to_sheet(dataList);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, filename);
    };
    const handleDownloadCollaborateurs = () => downloadExcel(RH_STATIC.departments.map(d=>({ Département:d.name, Effectif:d.count })),"Effectifs","Etat_Effectifs_Departements.xlsx");
    const getCollabInfo = (r) => {
        const u = users?.find(x => String(x.id) === String(r.employeeId || r.userId));
        return {
            matricule: r.employeeCode || u?.employeeCode || u?.matricule || "–",
            nom: r.employeeName || r.employeeFullName || r.collaborateur || u?.fullName || u?.employeeFullName || "–"
        };
    };

    const handleDownloadConges       = () => downloadExcel(filteredLeaves.map(r=>{ const info = getCollabInfo(r); return { Matricule: info.matricule, Collaborateur: info.nom, "Type de congé":r.absenceTypeNom||r.leaveTypeName||r.type||"–", "Date de début":r.dateDebut||r.startDate||"–", "Date de fin":r.dateFin||r.endDate||"–", "Nombre de jours":r.duree||r.numberOfDays||r.jours||"–", Statut:getStatusLabel(r.status||r.statut) }; }),"Congés","Etat_Demandes_Conges.xlsx");
    const handleDownloadDepenses     = () => downloadExcel(filteredFrais.map(r=>{ const info = getCollabInfo(r); return { Matricule: info.matricule, Collaborateur: info.nom, Catégorie:expenseCategories(r), Date:r.dateDepense||r.expenseDate||r.date||"–", "Montant (MAD)":r.montantDepense||r.totalAmount||r.montant||0, Statut:getStatusLabel(r.status||r.statut) }; }),"Notes de frais","Etat_Notes_De_Frais.xlsx");
    const handleDownloadMissions     = () => downloadExcel(filteredMissions.map(r=>{ const info = getCollabInfo(r); return { Matricule: info.matricule, Collaborateur: info.nom, "Nom de la mission":r.missionName||r.nomMission||r.titre||"–", Destination:r.ordreMissionLieu||r.ordreMissionVille||r.destination||"–", Type:r.natureMissionLibelle||r.ordreMissionLibelle||r.missionType||r.type||"–", "Date départ":r.ordreMissionDateDebut||r.departureDate||r.date||"–", Statut:getStatusLabel(r.status||r.statut) }; }),"Missions","Etat_Ordres_Mission.xlsx");
    const handleDownloadAvances      = () => downloadExcel(filteredAvances.map(r=>{ const info = getCollabInfo(r); return { Matricule: info.matricule, Collaborateur: info.nom, Type:r.avanceType||r.requestType||r.type||"–", "Montant (MAD)":r.montantDemande||r.montantInitial||0, "Nb mensualités":r.nombreMensualites||"–", Statut:getStatusLabel(r.status||r.statut) }; }),"Avances_Prets","Etat_Avances_Prets.xlsx");
    const handleDownloadAttestations = () => downloadExcel(filteredAttestations.map(r=>{ const info = getCollabInfo(r); return { Matricule: info.matricule, Collaborateur: info.nom, "Type":r.attestationName||r.type||"–", "Date de la demande":r.createdAt||r.dateDemande||"–", Statut:getStatusLabel(r.status||r.statut) }; }),"Attestations","Etat_Demandes_Attestations.xlsx");
    const handleDownloadPointage     = () => downloadExcel(pointageAnalytics.allEmployees.map(e=>({ Matricule:e.matricule, Collaborateur:e.nomPrenom, Section:e.section, "Jours attendus":e.expectedDays, "Jours travaillés":e.workedDays, Absences:e.absences, "Heures totales":e.totalHours.toFixed(1), "Heures supp.":e.overtimeHours.toFixed(1), Anomalies:e.anomalyCount, "Taux de présence":e.presenceRate+"%" })),"Pointage","Etat_Pointage_Mensuel.xlsx");

    const reportRowsInPeriod = (rows, dateGetter) => rows.filter((row) => isWithinDateRange(dateGetter(row), dateDebut, dateFin));
    const displayUserName = (user) => user?.fullName || user?.employeeFullName || [user?.prenom, user?.nom].filter(Boolean).join(" ") || user?.username || "–";
    const handleDownloadCollaborateursDynamic = () => downloadExcel(users.map((user) => ({
        Matricule: user.employeeCode || user.matricule || "–",
        Collaborateur: displayUserName(user),
        Poste: user.jobName || user.poste || "–",
        Entite: user.entityName || user.departmentName || user.departement || "-",
        Contrat: user.contractType || user.typeContrat || "–",
        "Date d'embauche": user.hireDate || user.dateEmbauche || "–",
        Statut: user.status || user.statut || (user.active === false ? "Inactif" : "Actif"),
    })), "Collaborateurs", "Etat_Collaborateurs.xlsx");
    const handleDownloadSalaires = () => downloadExcel(reportRowsInPeriod(reportData.salaires, (r) => r.dateEffet || r.createdAt).map((r) => ({
        Matricule: r.employeeCode || "-", Collaborateur: r.userFullName || "-", Poste: r.jobName || "-", Entite: r.entityName || "-",
        "Salaire brut (MAD)": r.salaireBrut ?? 0, "Salaire net (MAD)": r.salaireNet ?? 0, Devise: r.devise || "MAD",
        "Date d'effet": r.dateEffet || "–", "Date de fin": r.dateFin || "–", Motif: r.motif || "–",
    })), "Salaires", "Etat_Salaires.xlsx");
    const handleDownloadParcours = () => downloadExcel(reportRowsInPeriod(reportData.parcours, (r) => r.startDate || r.createdAt).map((r) => ({
        Collaborateur: r.userName || "–", "Parcours de carriÃ¨re": r.careerPath?.nom || r.careerPath?.name || "–",
        "Ã‰tape actuelle": r.currentStep?.nom || r.currentStep?.name || "–", "Date de début": r.startDate || "–", Statut: r.status || "–",
    })), "Parcours RH", "Etat_Parcours_RH.xlsx");
    const handleDownloadAvantages = () => downloadExcel(reportRowsInPeriod(reportData.avantages, (r) => r.updatedAt || r.createdAt).map((r) => ({
        Code: r.code || "-", Avantage: r.nom || "-", Categorie: r.categorieNom || "-", Description: r.description || "-",
        "Montant (MAD)": r.montant ?? 0, Frequence: r.frequence || "-", Statut: r.actif ? "Actif" : "Inactif",
    })), "Avantages sociaux", "Etat_Avantages_Sociaux.xlsx");
    const handleDownloadSoldesConges = () => downloadExcel(reportRowsInPeriod(reportData.soldesConges, (r) => r.updatedAt || r.createdAt).map((r) => ({
        Matricule: r.userEmployeeCode || "-", Collaborateur: r.userFullName || "-", Entite: r.entityName || "-", "Type de conge": r.absenceTypeNom || "-", Annee: r.annee || "-",
        "Solde initial": r.soldeInitial ?? 0, "Solde acquis": r.soldeAcquis ?? 0, "Solde consomme": r.soldeConsomme ?? 0, "Solde ajoute": r.soldeAjoute ?? 0, "Solde restant": r.soldeRestant ?? 0, "Date d'expiration": r.dateExpiration || "-",
    })), "Soldes de congés", "Etat_Soldes_Conges.xlsx");
    const handleDownloadMouvementsConges = () => downloadExcel(reportRowsInPeriod(reportData.mouvementsConges, (r) => r.dateMouvement).map((r) => ({
        Matricule: r.employeeCode || "-", Collaborateur: r.employeeName || "-", Entite: r.entityName || "-", "Type de conge": r.absenceTypeNom || "-", "Date du mouvement": r.dateMouvement || "-", Type: r.typeMouvement || "-", Valeur: r.valeur ?? 0,
        "Solde apres mouvement": r.soldeRestantSnapshot ?? 0, Reference: r.reference || "-", Commentaire: r.commentaire || "-",
    })), "Mouvements congés", "Etat_Mouvements_Conges.xlsx");
    const handleDownloadOffboarding = () => downloadExcel(offboardings.filter((r) => isWithinDateRange(r.dateDepart || r.date_depart, dateDebut, dateFin)).map((r) => {
        const steps = etapesByOffboarding[String(r.id)] || [];
        const pending = steps.filter((step) => !["COMPLETE", "COMPLETED", "TERMINE"].includes(String(step.statut || step.status || "").toUpperCase())).map((step) => step.nom || step.name).filter(Boolean);
        return { Matricule: r.employeeCode || "–", Collaborateur: r.employeeName || "–", Poste: r.employeeJobName || "–", "Date de départ": r.dateDepart || r.date_depart || "–", Motif: r.raisonDepart || r.raison_depart || r.motif || r.reason || "–", "Progression (%)": r.progression ?? 0, "Étapes restantes": pending.length, "Libellés des étapes restantes": pending.join(" | ") || "Aucune" };
    }), "Offboardings", "Etat_Offboarding.xlsx");
    const handleDownloadChecklistOffboarding = () => downloadExcel(offboardings.filter((r) => isWithinDateRange(r.dateDepart || r.date_depart, dateDebut, dateFin)).flatMap((r) => (etapesByOffboarding[String(r.id)] || []).map((step) => ({
        Matricule: r.employeeCode || "-", Collaborateur: r.employeeName || "-", "Date de depart": r.dateDepart || r.date_depart || "-", Ordre: step.ordreEtape ?? step.ordre ?? "-", Etape: step.nom || step.name || "-", Statut: step.statut || step.status || "EN_ATTENTE", Commentaire: step.commentaire || "-",
    }))), "Checklist offboarding", "Etat_Checklist_Offboarding.xlsx");
    const handleDownloadFormations = () => downloadExcel(reportRowsInPeriod(reportData.formations, (r) => r.updatedAt || r.createdAt).map((r) => ({
        Reference: r.reference || "-", Formation: r.nom || "-", Domaine: r.domaineNom || "-", Theme: r.themeNom || "-", Type: r.type || "-", Duree: r.duration ?? "-", "Type de duree": r.typeDuree || "-", "Cout (MAD)": r.cout ?? 0, "Capacite max.": r.nombreMaxGroupe ?? "-", Statut: r.status === 1 ? "Active" : "Inactive",
    })), "Formations", "Etat_Formations.xlsx");

    const downloadHandlers = {
        "etat-salaires":              handleDownloadSalaires,
        "etat-parcours":              handleDownloadParcours,
        "etat-avantages":             handleDownloadAvantages,
        "etat-soldes-conges":         handleDownloadSoldesConges,
        "etat-mouvements-conges":     handleDownloadMouvementsConges,
        "etat-offboarding":           handleDownloadOffboarding,
        "etat-checklist-offboarding": handleDownloadChecklistOffboarding,
        "etat-formations":            handleDownloadFormations,
        "etat-collaborateurs": handleDownloadCollaborateursDynamic,
        "etat-conges":         handleDownloadConges,
        "etat-depenses":       handleDownloadDepenses,
        "etat-missions":       handleDownloadMissions,
        "etat-avances":        handleDownloadAvances,
        "etat-attestations":   handleDownloadAttestations,
        "etat-pointage":       handleDownloadPointage,
    };

    // ════════════════════════════════════════════════════════════════════════
    //  RENDUS PAR ONGLET
    // ════════════════════════════════════════════════════════════════════════

    // ── RH & Effectifs ────────────────────────────────────────────────────────
    const renderRH = () => (
        <>
            <SectionTitle icon={<Users size={18} />}>RH &amp; Effectifs</SectionTitle>
            <div className="daGrid daGrid--4">
                <StatCard title="Employés"       value={RH_STATIC.totalEmployees}                     icon={<Users size={20} />}       color={COLORS.primary} />
                <StatCard title="Taux d'absentéisme"    value={`${RH_STATIC.absenteeismRate}%`}              icon={<Stethoscope size={20} />} color={COLORS.warning} />
                <StatCard title="Taux de mobilité int." value={`${RH_STATIC.internalMobility}%`}             icon={<TrendingUp size={20} />}  color={COLORS.success} />
                <StatCard title="Taux de turnover"      value={`${RH_STATIC.turnoverRate}%`}                 icon={<RefreshCw size={20} />}   color={COLORS.secondary} />
            </div>
            <div className="daGrid daGrid--4" style={{ marginTop:16 }}>
                <StatCard title="Départements"          value={RH_STATIC.departments.length}                 icon={<Building2 size={20} />}  color={COLORS.info} />
                <StatCard title="Salaire moyen (MAD)"   value={RH_STATIC.avgSalary.toLocaleString("fr-FR")} icon={<Banknote size={20} />}   color={COLORS.primary} />
                <StatCard title="Effectif CDI"          value={RH_STATIC.employeeStatus[0].value}            icon={<UserCheck size={20} />}  color={COLORS.success} />
                <StatCard title="Effectif CDD"          value={RH_STATIC.employeeStatus[1].value}            icon={<UserX size={20} />}      color={COLORS.warning} />
            </div>
            <div className="daGrid daGrid--2" style={{ marginTop:20 }}>
                <ChartCard title="Répartition par département">
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={RH_STATIC.departments}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <RechartsTooltip />
                            <Bar dataKey="count" fill={COLORS.primary} radius={[8,8,0,0]} name="Effectif" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Répartition par contrat">
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie data={RH_STATIC.employeeStatus} cx="50%" cy="50%" innerRadius={65} outerRadius={100} paddingAngle={4} dataKey="value" label={({name,percentage})=>`${name} ${percentage}%`}>
                                {RH_STATIC.employeeStatus.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]} />)}
                            </Pie>
                            <RechartsTooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
            <div className="daGrid daGrid--2" style={{ marginTop:16 }}>
                <ChartCard title="Répartition Hommes / Femmes">
                    <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                            <Pie data={RH_STATIC.genderData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({name,value})=>`${name}: ${value}`}>
                                {RH_STATIC.genderData.map((_,i)=><Cell key={i} fill={[COLORS.primary,COLORS.secondary][i]} />)}
                            </Pie>
                            <RechartsTooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Pyramide d'ancienneté">
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={RH_STATIC.ancienneteData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis type="number" />
                            <YAxis dataKey="range" type="category" width={80} />
                            <RechartsTooltip />
                            <Bar dataKey="count" fill={COLORS.teal} radius={[0,8,8,0]} name="Employés" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        </>
    );

    function PointageDayModal({ detail, onClose }) {
        if (!detail) return null;
        const { date, row, employee } = detail;
        const pointages = row?.pointages || [];
        const times = pointages.map(timeToMinutes).filter(t => t !== null);
        const amplitude = times.length >= 2 ? (times[times.length - 1] - times[0]) / 60 : 0;
        const labels = pointages.map((_, i) => i % 2 === 0 ? `Entrée ${Math.floor(i / 2) + 1}` : `Sortie ${Math.floor(i / 2) + 1}`);
        const dateLabel = new Date(`${date}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

        return (
            <div className="daPointageModalOverlay" onClick={onClose}>
                <div className="daPointageModal" onClick={e => e.stopPropagation()}>
                    <div className="daPointageModal__header">
                        <div>
                            <h4>{employee.nomPrenom}</h4>
                            <span>{dateLabel} · Matricule {employee.matricule}</span>
                        </div>
                        <button type="button" onClick={onClose}><X size={16} /></button>
                    </div>
                    <div className="daPointageModal__body">
                        {pointages.length ? (
                            <>
                                <div className="daPointageModal__list">
                                    {pointages.map((t, i) => (
                                        <div key={i} className="daPointageModal__row">
                                            <span className="daPointageModal__label">{labels[i]}</span>
                                            <span className="daPointageModal__time">{t}</span>
                                        </div>
                                    ))}
                                </div>
                                {amplitude > 0 && (
                                    <div className="daPointageModal__summary">
                                        Durée de présence totale : <strong>{formatHours(amplitude)}</strong>
                                    </div>
                                )}
                            </>
                        ) : (
                            <span style={{ color: "#9ca3af" }}>Aucun pointage enregistré ce jour-là.</span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── Congés ─────────────────────────────────────────────────────────────────
    const renderConges = () => {
        const normalizedLeaves = filteredLeaves.map(r => ({
            ...r,
            employeeFullName: r.employeeName || r.employeeFullName || r.collaborateur || "–",
            leaveTypeName: r.absenceTypeNom || r.leaveTypeName || r.type || "Autre",
            numberOfDays: r.duree || r.numberOfDays || r.jours || 0,
            startDate: r.dateDebut || r.startDate || "–",
            endDate: r.dateFin || r.endDate || "–",
        }));

        const total     = normalizedLeaves.length;
        const approved  = normalizedLeaves.filter(r=>(r.status||r.statut||"").toUpperCase().includes("APPROVED")).length;
        const pending   = normalizedLeaves.filter(r=>(r.status||r.statut||"").toUpperCase().includes("PENDING")).length;
        const rejected  = normalizedLeaves.filter(r=>(r.status||r.statut||"").toUpperCase().includes("REJECT")).length;
        const approvalRate = total ? Math.round((approved/total)*100) : 0;
        const avgDays   = total ? (normalizedLeaves.reduce((s,r)=>s+(r.numberOfDays||r.jours||0),0)/total).toFixed(1) : "0";

        const typeCount = {};
        normalizedLeaves.forEach(r=>{ const k=r.leaveTypeName||r.type||"Autre"; typeCount[k]=(typeCount[k]||0)+1; });
        const typeData = Object.entries(typeCount).map(([name,value])=>({name: t(name), value})).sort((a,b)=>b.value-a.value);

        const monthlyCount = {};
        normalizedLeaves.forEach(r=>{
            const d = getDate(r);
            if (d) { const m=d.slice(0,7); monthlyCount[m]=(monthlyCount[m]||0)+1; }
        });
        const monthlyData = Object.entries(monthlyCount).sort(([a],[b])=>a.localeCompare(b))
            .map(([mois,count])=>({mois:mois.slice(5),count}));

        const statusData = [
            { name:t("Approuvés"), value:approved },
            { name:t("En attente"), value:pending },
            { name:t("Refusés"), value:rejected },
        ].filter(d=>d.value>0);

        // Calcul du solde moyen restant du congé annuel par collaborateur : total des soldes / nombre de collaborateurs
        const employeeBalancesMap = new Map();
        normalizedLeaves.forEach(r => {
            const empKey = r.employeeId || r.employeeCode || r.employeeName || r.employeeFullName;
            if (!empKey) return;
            const isAnnual = !r.absenceTypeNom || r.absenceTypeNom.toLowerCase().includes("annuel") || (r.leaveTypeName && r.leaveTypeName.toLowerCase().includes("annuel"));
            const val = (r.soldeRestant !== undefined && r.soldeRestant !== null)
                ? Number(r.soldeRestant)
                : ((r.soldeRestantSnapshot !== undefined && r.soldeRestantSnapshot !== null) ? Number(r.soldeRestantSnapshot) : null);

            if (val !== null && !isNaN(val)) {
                if (!employeeBalancesMap.has(empKey) || isAnnual) {
                    employeeBalancesMap.set(empKey, val);
                }
            }
        });

        const employeeBalances = Array.from(employeeBalancesMap.values());
        const totalSoldeRestant = employeeBalances.reduce((sum, b) => sum + b, 0);
        const nbCollabsWithSolde = employeeBalances.length;
        const soldeMoyenRestant = nbCollabsWithSolde > 0
            ? (totalSoldeRestant / nbCollabsWithSolde).toFixed(1)
            : (normalizedLeaves.length > 0 ? "14.5" : "0");

        const recentLeaves = [...normalizedLeaves].sort((a,b)=>(getDate(b)||"").localeCompare(getDate(a)||"")).slice(0,8);

        return (
            <>
                <SectionTitle icon={<Palmtree size={18} />}>{t("Congés & Absences")}</SectionTitle>
                <DateFilterFields dateDebut={dateDebut} dateFin={dateFin} onChangeDebut={setDateDebut} onChangeFin={setDateFin} />

                <div className="daGrid daGrid--4">
                    <StatCard title="Demandes totales"     value={total}              icon={<CalendarCheck size={20}/>} color={COLORS.info}
                        onClick={() => setSelectedCongesCardModal({ title: "Toutes les demandes de congés", type: "total", rows: normalizedLeaves })} />
                    <StatCard title="Taux d'approbation"   value={`${approvalRate}%`} icon={<CheckCircle2 size={20}/>} color={COLORS.success}
                        onClick={() => setSelectedCongesCardModal({ title: "Historique & Taux d'approbation", type: "approvalRate", rows: normalizedLeaves })} />
                    <StatCard title="En attente"           value={pending}            icon={<Clock size={20}/>}        color={COLORS.warning}
                        onClick={() => setSelectedCongesCardModal({ title: "Demandes en attente", type: "pending", rows: normalizedLeaves.filter(r=>(r.status||r.statut||"").toUpperCase().includes("PENDING")) })} />
                    <StatCard title="Refusées"             value={rejected}           icon={<X size={20}/>}            color={COLORS.secondary}
                        onClick={() => setSelectedCongesCardModal({ title: "Demandes refusées", type: "rejected", rows: normalizedLeaves.filter(r=>(r.status||r.statut||"").toUpperCase().includes("REJECT")) })} />
                </div>
                <div className="daGrid daGrid--4" style={{ marginTop:16 }}>
                    <StatCard title="Approuvées"           value={approved}           icon={<CheckCircle2 size={20}/>} color={COLORS.success}
                        onClick={() => setSelectedCongesCardModal({ title: "Demandes approuvées", type: "approved", rows: normalizedLeaves.filter(r=>(r.status||r.statut||"").toUpperCase().includes("APPROVED")) })} />
                    <StatCard title="Durée moy. (jours)"  value={`${avgDays}j`}      icon={<CalendarRange size={20}/>}color={COLORS.primary}
                        onClick={() => setSelectedCongesCardModal({ title: "Durée des demandes de congés", type: "avgDays", rows: [...normalizedLeaves].sort((a,b)=>(b.numberOfDays||0)-(a.numberOfDays||0)) })} />
                    <StatCard title="Types de congé"      value={typeData.length}     icon={<Hash size={20}/>}         color={COLORS.purple}
                        onClick={() => setSelectedCongesCardModal({ title: "Répartition par type de congé", type: "typeCount", rows: normalizedLeaves, extra: { typeData, totalLeaves: total } })} />
                    <StatCard title="Solde moyen restant"  value={`${soldeMoyenRestant}j`} icon={<Palmtree size={20}/>} color={COLORS.teal} subtitle={nbCollabsWithSolde > 0 ? t("{{count}} collaborateurs", { count: nbCollabsWithSolde }) : "Congé annuel"}
                        onClick={() => setSelectedCongesCardModal({ title: "Solde restant du congé annuel par collaborateur", type: "soldeMoyen", rows: normalizedLeaves, extra: { employeeBalancesList: Array.from(employeeBalancesMap.entries()).map(([name, soldeRestant]) => ({ name, soldeRestant })) } })} />
                </div>
                <div className="daGrid daGrid--2" style={{ marginTop:20 }}>
                    <ChartCard title="Répartition par type de congé">
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie data={typeData} cx="50%" cy="50%" innerRadius={60} outerRadius={105} paddingAngle={3} dataKey="value" label={({name,value})=>`${name}: ${value}`}>
                                    {typeData.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]} />)}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                    <ChartCard title="Statuts des demandes">
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie data={statusData} cx="50%" cy="50%" outerRadius={105} dataKey="value" label={({name,value})=>`${name}: ${value}`}>
                                    <Cell fill={COLORS.success} />
                                    <Cell fill={COLORS.warning} />
                                    <Cell fill={COLORS.secondary} />
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
                {monthlyData.length > 0 && (
                    <ChartCard title="Évolution mensuelle des demandes de congés" fullWidth>
                        <ResponsiveContainer width="100%" height={260}>
                            <AreaChart data={monthlyData} margin={{ top: 30, right: 30, left: 10, bottom: 25 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="mois" label={{ value: "Mois", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <YAxis label={{ value: "Demandes", position: "top", offset: 10, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <RechartsTooltip />
                                <Area type="monotone" dataKey="count" stroke={COLORS.info} fill={`${COLORS.info}20`} name="Demandes" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}
                <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:18, marginTop:16 }}>
                    <h4 style={{ fontSize:14, fontWeight:600, color:"#1f2937", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                        <CalendarCheck size={16} color={COLORS.info} /> Dernières demandes
                    </h4>
                    <MiniTable
                        columns={[
                            { key:"employeeFullName", label:"Collaborateur", render:(v,r)=>v||r.collaborateur||"–" },
                            { key:"leaveTypeName",    label:"Type",          render:(v,r)=>t(v || r.type || "–") },
                            { key:"startDate",        label:"Début",         render:(v,r)=>v||r.dateDebut||"–" },
                            { key:"numberOfDays",     label:"Jours",         render:(v,r)=>v||r.jours||"–" },
                            { key:"status",           label:"Statut",        render:v=><StatusBadge statut={v} /> },
                        ]}
                        rows={recentLeaves}
                        emptyMsg="Aucune demande de congé trouvée"
                    />
                </div>
                <LeaveCardDetailModal modalData={selectedCongesCardModal} onClose={() => setSelectedCongesCardModal(null)} />
            </>
        );
    };

    const renderAvances = () => {
        const normalizedAvances = filteredAvances.map(r => ({
            ...r,
            employeeFullName: r.employeeName || r.employeeFullName || r.collaborateur || "–",
            requestType: r.avanceType || r.requestType || r.type || "Autre",
            montantDemande: r.montantDemande || r.montantInitial || 0,
            nombreMensualites: r.nombreMensualites || 0,
        }));

        const total      = normalizedAvances.length;
        const pending    = normalizedAvances.filter(r=>(r.status||r.statut||"").toUpperCase().includes("PENDING")).length;
        const approved   = normalizedAvances.filter(r=>(r.status||r.statut||"").toUpperCase().includes("APPROVED")).length;
        const authorizedAmount = (r) => Number(r.montantAutorise ?? r.montantAccorde ?? r.montantApproved ?? r.montantTotal ?? r.montantDemande ?? r.montantInitial ?? 0);
        const totalMontant = normalizedAvances.filter(r => (r.status || r.statut || "").toUpperCase().includes("APPROVED")).reduce((sum, r) => sum + authorizedAmount(r), 0);
        const avgAmount  = total ? Math.round(normalizedAvances.reduce((sum, r) => sum + Number(r.montantDemande || r.montantInitial || 0), 0) / total) : 0;
        const avanceColumns = [
            { key: "employeeFullName", label: "Collaborateur", render: (v, r) => v || r.employeeName || "–" },
            { key: "employeeMatricule", label: "Matricule", render: (v) => v || "–" },
            { key: "requestType", label: "Type", render: (v, r) => v || r.avanceType || "–" },
            { key: "montantDemande", label: "Montant demandé", render: (v) => `${Number(v || 0).toLocaleString("fr-FR")} MAD` },
            { key: "montantTotal", label: "Montant autorisé", render: (_, r) => `${authorizedAmount(r).toLocaleString("fr-FR")} MAD` },
            { key: "status", label: "Statut", render: (v, r) => <StatusBadge statut={v || r.statut} /> },
        ];
        const openAvanceDetails = (title, rows) => openCardDetails(title, rows, avanceColumns, "Aucune demande correspondante");

        const typeCount = {};
        normalizedAvances.forEach(r=>{ const k=r.requestType||r.type||"Autre"; typeCount[k]=(typeCount[k]||0)+1; });
        const typeData = Object.entries(typeCount).map(([name,value])=>({name,value}));

        const montantParType = {};
        normalizedAvances.forEach(r=>{ const k=r.requestType||r.type||"Autre"; montantParType[k]=(montantParType[k]||0)+(r.montantDemande||r.montantInitial||0); });
        const montantData = Object.entries(montantParType).map(([name,amount])=>({name,amount}));

        const mensualitesDist = {};
        normalizedAvances.forEach(r=>{ const k=String(r.nombreMensualites||"N/A")+" mois"; mensualitesDist[k]=(mensualitesDist[k]||0)+1; });
        const mensualitesData = Object.entries(mensualitesDist).map(([name,count])=>({name,count})).sort((a,b)=>a.name.localeCompare(b.name));

        const recentAvances = [...normalizedAvances].sort((a,b)=>(getDate(b)||"").localeCompare(getDate(a)||"")).slice(0,8);


        return (
            <>
                <SectionTitle icon={<HandCoins size={18} />}>Avances &amp; Prêts</SectionTitle>
                <DateFilterFields dateDebut={dateDebut} dateFin={dateFin} onChangeDebut={setDateDebut} onChangeFin={setDateFin} />
                <div className="daGrid daGrid--4">
                    <StatCard title="Total Accordé"       value={fmtMAD(totalMontant)} icon={<Landmark size={20}/>} color={COLORS.primary} onClick={() => openAvanceDetails("Demandes approuvées — montants autorisés", normalizedAvances.filter(r => (r.status || r.statut || "").toUpperCase().includes("APPROVED")))} />
                    <StatCard title="Demandes en attente" value={pending} icon={<Clock size={20}/>} color={COLORS.warning} onClick={() => openAvanceDetails("Demandes d'avance et prêt en attente", normalizedAvances.filter(r => (r.status || r.statut || "").toUpperCase().includes("PENDING")))} />
                    <StatCard title="Demandes approuvées" value={approved} icon={<CheckCircle2 size={20}/>} color={COLORS.success} onClick={() => openAvanceDetails("Demandes d'avance et prêt approuvées", normalizedAvances.filter(r => (r.status || r.statut || "").toUpperCase().includes("APPROVED")))} />
                    <StatCard title="Montant moy/demande" value={`${fmtK(avgAmount)} MAD`} icon={<Wallet size={20}/>} color={COLORS.info} onClick={() => openAvanceDetails("Toutes les demandes d'avance et prêt", normalizedAvances)} />
                </div>
                <div className="daGrid daGrid--4" style={{ marginTop:16 }}>
                    <StatCard title="Demandes totales" value={total} icon={<FileDown size={20}/>} color={COLORS.teal} onClick={() => openAvanceDetails("Toutes les demandes d'avance et prêt", normalizedAvances)} />
                    <StatCard title="Total des avances" value={normalizedAvances.filter(r => (r.requestType || r.type || "").toLowerCase().includes("avance")).length} icon={<Banknote size={20}/>} color={COLORS.purple} onClick={() => openAvanceDetails("Demandes d'avance", normalizedAvances.filter(r => (r.requestType || r.type || "").toLowerCase().includes("avance")))} />
                    <StatCard title="Total des prêts" value={normalizedAvances.filter(r => (r.requestType || r.type || "").toLowerCase().includes("pret") || (r.requestType || r.type || "").toLowerCase().includes("prêt")).length} icon={<CreditCard size={20}/>} color={COLORS.secondary} onClick={() => openAvanceDetails("Demandes de prêt", normalizedAvances.filter(r => (r.requestType || r.type || "").toLowerCase().includes("pret") || (r.requestType || r.type || "").toLowerCase().includes("prêt")))} />
                    <StatCard title="Montant max" value={fmtMAD(Math.max(0, ...normalizedAvances.map(authorizedAmount)))} icon={<TrendingUp size={20}/>} color={COLORS.warning} onClick={() => openAvanceDetails("Demandes classées par montant autorisé", [...normalizedAvances].sort((a, b) => authorizedAmount(b) - authorizedAmount(a)))} />
                </div>
                <div className="daGrid daGrid--2" style={{ marginTop:20 }}>
                    <ChartCard title="Répartition Avance vs Prêt">
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie data={typeData} cx="50%" cy="50%" outerRadius={105} dataKey="value" label={({name,value})=>`${name}: ${value}`}>
                                    <Cell fill={COLORS.primary} />
                                    <Cell fill={COLORS.secondary} />
                                    {typeData.slice(2).map((_,i)=><Cell key={i} fill={CHART_COLORS[i+2]} />)}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                    <ChartCard title="Montants par type">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={montantData} margin={{ top: 30, right: 20, left: 10, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" label={{ value: "Type", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <YAxis tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} label={{ value: "MAD", position: "top", offset: 10, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <RechartsTooltip formatter={v=>[`${fmtK(v)} MAD`,"Montant"]} />
                                <Bar dataKey="amount" fill={COLORS.info} radius={[8,8,0,0]} name="Montant (MAD)" />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
                {/*{mensualitesData.length > 0 && (*/}
                {/*    <ChartCard title="Distribution du nombre de mensualités" fullWidth>*/}
                {/*        <ResponsiveContainer width="100%" height={220}>*/}
                {/*            <BarChart data={mensualitesData}>*/}
                {/*                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />*/}
                {/*                <XAxis dataKey="name" />*/}
                {/*                <YAxis />*/}
                {/*                <RechartsTooltip />*/}
                {/*                <Bar dataKey="count" fill={COLORS.purple} radius={[8,8,0,0]} name="Demandes" />*/}
                {/*            </BarChart>*/}
                {/*        </ResponsiveContainer>*/}
                {/*    </ChartCard>*/}
                {/*)}*/}
                <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:18, marginTop:16 }}>
                    <h4 style={{ fontSize:14, fontWeight:600, color:"#1f2937", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                        <HandCoins size={16} color={COLORS.primary} /> Dernières demandes
                    </h4>
                    <MiniTable
                        columns={[
                            { key:"employeeFullName", label:"Collaborateur",    render:(v,r)=>v||"–" },
                            { key:"requestType",      label:"Type",             render:(v,r)=>v||r.type||"–" },
                            { key:"montantDemande",   label:"Montant (MAD)",    render:(v,r)=>(v||r.montantInitial||0).toLocaleString("fr-FR") },
                            { key:"nombreMensualites",label:"Mensualités",      render:v=>v||"–" },
                            { key:"status",           label:"Statut",           render:v=><StatusBadge statut={v} /> },
                        ]}
                        rows={recentAvances}
                        emptyMsg="Aucune demande d'avance ou prêt trouvée"
                    />
                </div>
            </>
        );
    };

    const renderFrais = () => {
        const normalizedFrais = filteredFrais.map(r => ({
            ...r,
            employeeFullName: r.employeeName || r.employeeFullName || r.collaborateur || "–",
            category: expenseCategories(r),
            totalAmount: r.montantDepense || r.totalAmount || r.montant || 0,
            expenseDate: r.dateDepense || r.expenseDate || r.date || "–",
        }));

        const total      = normalizedFrais.length;
        const totalAmt   = normalizedFrais.reduce((s,r)=>s+(r.totalAmount||r.montant||0),0);
        const pending    = normalizedFrais.filter(r=>(r.status||r.statut||"").toUpperCase().includes("PENDING")).length;
        const approved   = normalizedFrais.filter(r=>(r.status||r.statut||"").toUpperCase().includes("APPROVED")).length;
        const avgAmt     = total ? Math.round(totalAmt/total) : 0;
        const maxAmt     = Math.max(0,...normalizedFrais.map(r=>r.totalAmount||r.montant||0));
        const avgProcessingDays = (() => { const rows = normalizedFrais.filter(r => r.createdAt && r.updatedAt); return rows.length ? (rows.reduce((sum, r) => sum + Math.max(0, (new Date(r.updatedAt) - new Date(r.createdAt)) / 86400000), 0) / rows.length).toFixed(1) : "0"; })();

        const categories = {};
        normalizedFrais.forEach(r=>{ const k=r.category||r.categorie||"Autre"; categories[k]=(categories[k]||0)+(r.totalAmount||r.montant||0); });
        const catData = Object.entries(categories).map(([name,amount])=>({name,amount})).sort((a,b)=>b.amount-a.amount);

        const statusDist = {};
        normalizedFrais.forEach(r=>{ const k=getStatusLabel(r.status||r.statut||"Autre"); statusDist[k]=(statusDist[k]||0)+1; });
        const statusData = Object.entries(statusDist).map(([name,value])=>({name,value}));

        const monthly = {};
        normalizedFrais.forEach(r=>{ const d=getDate(r); if(d){const m=d.slice(0,7); monthly[m]=(monthly[m]||0)+(r.totalAmount||r.montant||0);} });
        const monthlyData = Object.entries(monthly).sort(([a],[b])=>a.localeCompare(b)).map(([mois,amount])=>({mois:mois.slice(5),amount}));

        const recent = [...normalizedFrais].sort((a,b)=>(getDate(b)||"").localeCompare(getDate(a)||"")).slice(0,8);
        const fraisColumns = [
            { key: "employeeFullName", label: "Collaborateur", render: (v) => v || "–" },
            { key: "category", label: "Catégorie", render: (v) => v || "–" },
            { key: "expenseDate", label: "Date", render: (v) => v || "–" },
            { key: "totalAmount", label: "Montant", render: (v) => `${Number(v || 0).toLocaleString("fr-FR")} MAD` },
            { key: "status", label: "Statut", render: (v, r) => <StatusBadge statut={v || r.statut} /> },
        ];
        const openFraisDetails = (title, rows) => openCardDetails(title, rows, fraisColumns, "Aucune note de frais correspondante");


        return (
            <>
                <SectionTitle icon={<Wallet size={18} />}>Notes de Frais</SectionTitle>
                <DateFilterFields dateDebut={dateDebut} dateFin={dateFin} onChangeDebut={setDateDebut} onChangeFin={setDateFin} />
                <div className="daGrid daGrid--4">
                    <StatCard title="Dépense totale"      value={fmtMAD(totalAmt)}          icon={<Wallet size={20}/>}        color={COLORS.success} onClick={() => openFraisDetails("Toutes les dépenses", [...normalizedFrais].sort((a, b) => b.totalAmount - a.totalAmount))} />
                    <StatCard title="Demandes en attente" value={pending}                    icon={<AlertTriangle size={20}/>} color={COLORS.warning} onClick={() => openFraisDetails("Notes de frais en attente", normalizedFrais.filter(r => (r.status || r.statut || "").toUpperCase().includes("PENDING")))} />
                    <StatCard title="Demandes approuvées" value={approved}                   icon={<CheckCircle2 size={20}/>}  color={COLORS.info} onClick={() => openFraisDetails("Notes de frais approuvées", normalizedFrais.filter(r => (r.status || r.statut || "").toUpperCase().includes("APPROVED")))} />
                    <StatCard title="Montant moyen"       value={`${fmtK(avgAmt)} MAD`}     icon={<Receipt size={20}/>}       color={COLORS.primary} onClick={() => openFraisDetails("Notes de frais — montant moyen", normalizedFrais)} />
                </div>
                <div className="daGrid daGrid--4" style={{ marginTop:16 }}>
                    <StatCard title="Demandes totales"    value={total}                      icon={<FileText size={20}/>}      color={COLORS.teal} onClick={() => openFraisDetails("Toutes les notes de frais", normalizedFrais)} />
                    <StatCard title="Catégories"          value={catData.length}             icon={<Hash size={20}/>}          color={COLORS.purple} onClick={() => openFraisDetails("Notes de frais par catégorie", [...normalizedFrais].sort((a, b) => a.category.localeCompare(b.category)))} />
                    <StatCard title="Note max"            value={`${fmtK(maxAmt)} MAD`}     icon={<TrendingUp size={20}/>}    color={COLORS.secondary} onClick={() => openFraisDetails("Notes de frais classées par montant", [...normalizedFrais].sort((a, b) => b.totalAmount - a.totalAmount))} />
                    <StatCard title="Délai moyen" value={`${avgProcessingDays}j`} icon={<Clock size={20}/>} color={COLORS.warning} subtitle="Calculé sur les demandes BDD" onClick={() => openFraisDetails("Notes de frais traitées", normalizedFrais.filter(r => r.createdAt && r.updatedAt))} />
                </div>
                <div className="daGrid daGrid--2" style={{ marginTop:20 }}>
                    <ChartCard title="Top catégories de dépenses (MAD)">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={catData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis type="number" tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} label={{ value: "MAD", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <YAxis dataKey="name" type="category" width={130} />
                                <RechartsTooltip formatter={v=>[`${fmtK(v)} MAD`,"Montant"]} />
                                <Bar dataKey="amount" fill={COLORS.secondary} radius={[0,8,8,0]} name="Montant (MAD)" />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                    <ChartCard title="Répartition des statuts">
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={100} paddingAngle={3} dataKey="value" label={({name,value})=>`${value}`}>
                                    {statusData.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]} />)}
                                </Pie>
                                <Legend />
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
                {monthlyData.length > 0 && (
                    <ChartCard title="Évolution mensuelle des dépenses" fullWidth>
                        <ResponsiveContainer width="100%" height={260}>
                            <AreaChart data={monthlyData} margin={{ top: 30, right: 30, left: 10, bottom: 25 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="mois" label={{ value: "Mois", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <YAxis tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} label={{ value: "MAD", position: "top", offset: 10, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <RechartsTooltip formatter={v=>[`${fmtK(v)} MAD`,"Dépenses"]} />
                                <Area type="monotone" dataKey="amount" stroke={COLORS.success} fill={`${COLORS.success}20`} name="Montant (MAD)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}
                <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:18, marginTop:16 }}>
                    <h4 style={{ fontSize:14, fontWeight:600, color:"#1f2937", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                        <Wallet size={16} color={COLORS.success} /> Dernières notes de frais
                    </h4>
                    <MiniTable
                        columns={[
                            { key:"employeeFullName", label:"Collaborateur",  render:(v,r)=>v||"–" },
                            { key:"category",         label:"Catégorie",      render:(v,r)=>v||r.categorie||"–" },
                            { key:"totalAmount",      label:"Montant (MAD)",  render:(v,r)=>(v||r.montant||0).toLocaleString("fr-FR") },
                            { key:"expenseDate",      label:"Date",           render:(v,r)=>v||r.date||"–" },
                            { key:"status",           label:"Statut",         render:v=><StatusBadge statut={v} /> },
                        ]}
                        rows={recent}
                        emptyMsg="Aucune note de frais trouvée"
                    />
                </div>
            </>
        );
    };

    const renderMissions = () => {
        const normalizedMissions = filteredMissions.map(r => ({
            ...r,
            employeeFullName: r.employeeName || r.employeeFullName || r.collaborateur || "–",
            missionType: r.natureMissionLibelle || r.ordreMissionLibelle || r.missionType || r.type || "Autre",
            destination: r.ordreMissionLieu || r.ordreMissionVille || r.destination || "–",
            departureDate: r.ordreMissionDateDebut || r.departureDate || r.date || "–",
        }));

        const total     = normalizedMissions.length;
        const ongoing   = normalizedMissions.filter(r=>(r.status||r.statut||"").toUpperCase().includes("PROGRESS")||(r.status||r.statut||"").toUpperCase()==="IN_PROGRESS").length;
        const completed = normalizedMissions.filter(r=>(r.status||r.statut||"").toUpperCase()==="COMPLETED"||(r.status||r.statut||"").toUpperCase().includes("TERMINÉE")).length;
        const pending   = normalizedMissions.filter(r=>(r.status||r.statut||"").toUpperCase().includes("PENDING")).length;
        const distinctDestinations = new Set(normalizedMissions.map(r => r.destination).filter(destination => destination && destination !== "–")).size;
        const distinctEmployees = new Set(normalizedMissions.map(r => r.employeeFullName).filter(name => name && name !== "–")).size;
        const plannedMissions = normalizedMissions.filter(r => r.departureDate && r.departureDate !== "–").length;

        const byType = {};
        normalizedMissions.forEach(r=>{ const k=r.missionType||r.type||"Autre"; byType[k]=(byType[k]||0)+1; });
        const typeData = Object.entries(byType).map(([type,count])=>({type,count}));

        const byDestination = {};
        normalizedMissions.forEach(r=>{ const k=r.destination||"Non renseignée"; byDestination[k]=(byDestination[k]||0)+1; });
        const destinationData = Object.entries(byDestination).map(([name,count])=>({name,count})).sort((a,b)=>b.count-a.count);

        const monthly = {};
        normalizedMissions.forEach(r=>{ const d=getDate(r); if(d){const m=d.slice(0,7); monthly[m]=(monthly[m]||0)+1;} });
        const monthlyData = Object.entries(monthly).sort(([a],[b])=>a.localeCompare(b)).map(([mois,count])=>({mois:mois.slice(5),count}));

        const recent = [...normalizedMissions].sort((a,b)=>(getDate(b)||"").localeCompare(getDate(a)||"")).slice(0,8);
        const missionColumns = [
            { key: "employeeFullName", label: "Collaborateur", render: (v) => v || "–" },
            { key: "missionType", label: "Type", render: (v) => v || "–" },
            { key: "destination", label: "Destination", render: (v) => v || "–" },
            { key: "departureDate", label: "Départ", render: (v) => v || "–" },
            { key: "status", label: "Statut", render: (v, r) => <StatusBadge statut={v || r.statut} /> },
        ];
        const openMissionDetails = (title, rows) => openCardDetails(title, rows, missionColumns, "Aucun ordre de mission correspondant");


        return (
            <>
                <SectionTitle icon={<PlaneTakeoff size={18} />}>Ordres de Mission</SectionTitle>
                <DateFilterFields dateDebut={dateDebut} dateFin={dateFin} onChangeDebut={setDateDebut} onChangeFin={setDateFin} />
                <div className="daGrid daGrid--4">
                    <StatCard title="Missions en cours"   value={ongoing}                  icon={<PlaneTakeoff size={20}/>} color={COLORS.info} onClick={() => openMissionDetails("Missions en cours", normalizedMissions.filter(r => { const status = (r.status || r.statut || "").toUpperCase(); return status.includes("PROGRESS") || status === "IN_PROGRESS"; }))} />
                    <StatCard title="Missions terminées"  value={completed}                icon={<CheckCircle2 size={20}/>} color={COLORS.success} onClick={() => openMissionDetails("Missions terminées", normalizedMissions.filter(r => { const status = (r.status || r.statut || "").toUpperCase(); return status === "COMPLETED" || status.includes("TERMIN"); }))} />
                    <StatCard title="Destinations"        value={distinctDestinations}     icon={<PlaneTakeoff size={20}/>} color={COLORS.secondary} onClick={() => openMissionDetails("Missions par destination", normalizedMissions)} />
                    <StatCard title="Collaborateurs"      value={distinctEmployees}        icon={<Users size={20}/>}        color={COLORS.primary} onClick={() => openMissionDetails("Collaborateurs en mission", normalizedMissions)} />
                </div>
                <div className="daGrid daGrid--4" style={{ marginTop:16 }}>
                    <StatCard title="Demandes totales"    value={total}                    icon={<FileText size={20}/>}     color={COLORS.teal} onClick={() => openMissionDetails("Toutes les demandes de mission", normalizedMissions)} />
                    <StatCard title="En attente"          value={pending}                  icon={<Clock size={20}/>}        color={COLORS.warning} onClick={() => openMissionDetails("Missions en attente", normalizedMissions.filter(r => (r.status || r.statut || "").toUpperCase().includes("PENDING")))} />
                    <StatCard title="Types de mission"    value={typeData.length}          icon={<Hash size={20}/>}         color={COLORS.purple} onClick={() => openMissionDetails("Missions par type", [...normalizedMissions].sort((a, b) => a.missionType.localeCompare(b.missionType)))} />
                    <StatCard title="Missions planifiées" value={plannedMissions}          icon={<CalendarRange size={20}/>} color={COLORS.error} onClick={() => openMissionDetails("Missions planifiées", normalizedMissions.filter(r => r.departureDate && r.departureDate !== "–"))} />
                </div>
                <div className="daGrid daGrid--2" style={{ marginTop:20 }}>
                    <ChartCard title="Répartition par ordre de mission">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={typeData} margin={{ top: 30, right: 20, left: 10, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="type" label={{ value: "Type d'ordre de mission", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <YAxis label={{ value: "Demandes", position: "top", offset: 10, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <RechartsTooltip />
                                <Bar dataKey="count" fill={COLORS.primary} radius={[8,8,0,0]} name="Demandes" />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                    <ChartCard title="Répartition par destination">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={destinationData} margin={{ top: 30, right: 20, left: 10, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" label={{ value: "Destination", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <YAxis allowDecimals={false} label={{ value: "Demandes", position: "top", offset: 10, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <RechartsTooltip />
                                <Bar dataKey="count" fill={COLORS.secondary} radius={[8,8,0,0]} name="Demandes" />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
                {monthlyData.length > 0 && (
                    <ChartCard title="Évolution mensuelle des ordres de mission" fullWidth>
                        <ResponsiveContainer width="100%" height={260}>
                            <AreaChart data={monthlyData} margin={{ top: 30, right: 30, left: 10, bottom: 25 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="mois" label={{ value: "Mois", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <YAxis label={{ value: "Demandes", position: "top", offset: 10, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <RechartsTooltip />
                                <Area type="monotone" dataKey="count" stroke="#2a5ca9" strokeWidth={1.25} fill="#f1f5fb" fillOpacity={1} name="Demandes" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}
                <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:18, marginTop:16 }}>
                    <h4 style={{ fontSize:14, fontWeight:600, color:"#1f2937", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                        <PlaneTakeoff size={16} color={COLORS.primary} /> Dernières missions
                    </h4>
                    <MiniTable
                        columns={[
                            { key:"employeeFullName", label:"Collaborateur", render:(v,r)=>v||"–" },
                            { key:"missionType",      label:"Type",          render:(v,r)=>v||r.type||"–" },
                            { key:"destination",      label:"Destination",   render:(v,r)=>v||"–" },
                            { key:"departureDate",    label:"Départ",         render:v=>v||"–" },
                            { key:"status",           label:"Statut",        render:v=><StatusBadge statut={v} /> },
                        ]}
                        rows={recent}
                        emptyMsg="Aucun ordre de mission trouvé"
                    />
                </div>
            </>
        );
    };

    const renderAttestations = () => {
        const normalizedAttestations = filteredAttestations.map(r => ({
            ...r,
            employeeFullName: r.fullName || (r.employeeNom && r.employeePrenom ? `${r.employeePrenom} ${r.employeeNom}` : null) || r.employeeFullName || r.collaborateur || "–",
            attestationName: r.attestationTitre || r.attestationName || r.type || "Autre",
            createdAt: r.dateDemande || r.createdAt || "–",
        }));

        const total     = normalizedAttestations.length;
        const pending   = normalizedAttestations.filter(r=>(r.status||r.statut||"").toUpperCase().includes("PENDING")).length;
        const delivered = normalizedAttestations.filter(r=>(r.status||r.statut||"").toUpperCase().includes("APPROVED")||(r.status||r.statut||"").toUpperCase().includes("DELIVERED")).length;
        const rejected  = normalizedAttestations.filter(r=>(r.status||r.statut||"").toUpperCase().includes("REJECT")).length;
        const deliveryRate = total ? Math.round((delivered/total)*100) : 0;
        const avgProcessingDays = (() => { const rows = normalizedAttestations.filter(r => r.createdAt && r.updatedAt); return rows.length ? (rows.reduce((sum, r) => sum + Math.max(0, (new Date(r.updatedAt) - new Date(r.createdAt)) / 86400000), 0) / rows.length).toFixed(1) : "0"; })();

        const typeCount = {};
        normalizedAttestations.forEach(r=>{ const k=r.attestationName||r.type||"Autre"; typeCount[k]=(typeCount[k]||0)+1; });
        const typeData = Object.entries(typeCount).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value);

        const statusDist = {};
        normalizedAttestations.forEach(r=>{ const k=getStatusLabel(r.status||r.statut||"Autre"); statusDist[k]=(statusDist[k]||0)+1; });
        const statusData = Object.entries(statusDist).map(([name,value])=>({name,value}));

        const monthly = {};
        normalizedAttestations.forEach(r=>{ const d=getDate(r); if(d){const m=d.slice(0,7); monthly[m]=(monthly[m]||0)+1;} });
        const monthlyData = Object.entries(monthly).sort(([a],[b])=>a.localeCompare(b)).map(([mois,count])=>({mois:mois.slice(5),count}));

        const recent = [...normalizedAttestations].sort((a,b)=>(getDate(b)||"").localeCompare(getDate(a)||"")).slice(0,8);
        const attestationColumns = [
            { key: "employeeFullName", label: "Collaborateur", render: (v) => v || "–" },
            { key: "attestationName", label: "Type d'attestation", render: (v) => v || "–" },
            { key: "createdAt", label: "Date de demande", render: (v) => v || "–" },
            { key: "status", label: "Statut", render: (v, r) => <StatusBadge statut={v || r.statut} /> },
        ];
        const openAttestationDetails = (title, rows) => openCardDetails(title, rows, attestationColumns, "Aucune demande d'attestation correspondante");


        return (
            <>
                <SectionTitle icon={<FileText size={18} />}>Attestations</SectionTitle>
                <DateFilterFields dateDebut={dateDebut} dateFin={dateFin} onChangeDebut={setDateDebut} onChangeFin={setDateFin} />
                <div className="daGrid daGrid--4">
                    <StatCard title="Demandes totales"   value={total}               icon={<FileText size={20}/>}      color={COLORS.primary} onClick={() => openAttestationDetails("Toutes les demandes d'attestation", normalizedAttestations)} />
                    <StatCard title="En attente"         value={pending}             icon={<Clock size={20}/>}         color={COLORS.warning} onClick={() => openAttestationDetails("Attestations en attente", normalizedAttestations.filter(r => (r.status || r.statut || "").toUpperCase().includes("PENDING")))} />
                    <StatCard title="Délivrées"          value={delivered}           icon={<CheckCircle2 size={20}/>}  color={COLORS.success} onClick={() => openAttestationDetails("Attestations délivrées", normalizedAttestations.filter(r => { const status = (r.status || r.statut || "").toUpperCase(); return status.includes("APPROVED") || status.includes("DELIVERED"); }))} />
                    <StatCard title="Taux de livraison"  value={`${deliveryRate}%`}  icon={<Activity size={20}/>}      color={COLORS.info} onClick={() => openAttestationDetails("Demandes délivrées — taux de livraison", normalizedAttestations.filter(r => { const status = (r.status || r.statut || "").toUpperCase(); return status.includes("APPROVED") || status.includes("DELIVERED"); }))} />
                </div>
                <div className="daGrid daGrid--4" style={{ marginTop:16 }}>
                    <StatCard title="Refusées"           value={rejected}            icon={<X size={20}/>}             color={COLORS.secondary} onClick={() => openAttestationDetails("Attestations refusées", normalizedAttestations.filter(r => (r.status || r.statut || "").toUpperCase().includes("REJECT")))} />
                    <StatCard title="Types d'attestation" value={typeData.length}   icon={<Hash size={20}/>}           color={COLORS.purple} onClick={() => openAttestationDetails("Demandes par type d'attestation", [...normalizedAttestations].sort((a, b) => a.attestationName.localeCompare(b.attestationName)))} />
                    <StatCard title="Délai moyen" value={`${avgProcessingDays}j`} icon={<Timer size={20}/>} color={COLORS.teal} subtitle="Calculé sur les demandes BDD" onClick={() => openAttestationDetails("Attestations traitées", normalizedAttestations.filter(r => r.createdAt && r.updatedAt))} />
                    <StatCard title="Taux d'approbation" value={`${deliveryRate}%`} icon={<Star size={20}/>}          color={COLORS.success} onClick={() => openAttestationDetails("Attestations approuvées", normalizedAttestations.filter(r => (r.status || r.statut || "").toUpperCase().includes("APPROVED")))} />
                </div>
                <div className="daGrid daGrid--2" style={{ marginTop:20 }}>
                    <ChartCard title="Demandes par type d'attestation">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={typeData} margin={{ top: 30, right: 20, left: 10, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" label={{ value: "Type d'attestation", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <YAxis label={{ value: "Demandes", position: "top", offset: 10, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <RechartsTooltip />
                                <Bar dataKey="value" fill={COLORS.info} radius={[8,8,0,0]} name="Demandes" />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                    <ChartCard title="Répartition des statuts">
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie data={statusData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">
                                    {statusData.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]} />)}
                                </Pie>
                                <Legend />
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
                {monthlyData.length > 0 && (
                    <ChartCard title="Évolution mensuelle des demandes d'attestations" fullWidth>
                        <ResponsiveContainer width="100%" height={260}>
                            <AreaChart data={monthlyData} margin={{ top: 30, right: 30, left: 10, bottom: 25 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="mois" label={{ value: "Mois", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <YAxis label={{ value: "Demandes", position: "top", offset: 10, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} />
                                <RechartsTooltip />
                                <Area type="monotone" dataKey="count" stroke={COLORS.purple} fill={`${COLORS.purple}20`} name="Demandes" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}
                <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:18, marginTop:16 }}>
                    <h4 style={{ fontSize:14, fontWeight:600, color:"#1f2937", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                        <FileText size={16} color={COLORS.info} /> Dernières demandes
                    </h4>
                    <MiniTable
                        columns={[
                            { key:"employeeFullName",  label:"Collaborateur",      render:(v,r)=>v||"–" },
                            { key:"attestationName",   label:"Type d'attestation", render:(v,r)=>v||r.type||"–" },
                            { key:"createdAt",         label:"Date de la demande",    render:(v,r)=>v||r.dateDemande||"–" },
                            { key:"status",            label:"Statut",             render:v=><StatusBadge statut={v} /> },
                        ]}
                        rows={recent}
                        emptyMsg="Aucune demande d'attestation trouvée"
                    />
                </div>
            </>
        );
    };

    // ── Pointage ───────────────────────────────────────────────────────────────
    const renderPointageImportPanel = () => (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:"#f8fafc", padding:"16px 20px", borderRadius:12, border:"1px dashed #cbd5e1", marginBottom:25 }}>
            <div style={{ display:"flex", alignItems:"center", gap:15 }}>
                <FileSpreadsheet size={24} color="#6366f1" />
                <div style={{ display:"flex", flexDirection:"column" }}>
                    <strong style={{ fontSize:15, color:"#1e293b" }}>
                        {pointagePayload?.fileNames?.length ? t("{{count}} fichier(s) importé(s)", { count: pointagePayload.fileNames.length }) : (pointagePayload?.fileName || t("Aucun fichier importé"))}
                    </strong>
                    <span style={{ fontSize:13, color:"#64748b", marginTop:2 }}>
                        {pointagePayload?.fileNames?.length ? pointagePayload.fileNames.join(", ") : (pointagePayload?.importedAt ? t("Importé le {{date}} · {{count}} lignes", { date: new Date(pointagePayload.importedAt).toLocaleString(i18n.language), count: pointagePayload.rows?.length || 0 }) : t("Les fichiers s'accumuleront ici et resteront disponibles."))}
                    </span>
                </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <label htmlFor="da-pointage-file" style={{ cursor:"pointer", background:"#315f97", color:"#fff", padding:"8px 16px", borderRadius:8, fontSize:13.5, fontWeight:500, display:"inline-block" }}>
                    {pointageImporting ? t("Import en cours...") : t("Ajouter un fichier")}
                    <input id="da-pointage-file" type="file" accept=".xlsx,.xls,.csv" disabled={pointageImporting} style={{ display:"none" }} onChange={ev=>{const f=ev.target.files?.[0]; if(f) handlePointageImport(f); ev.target.value="";}} />
                </label>
                {pointagePayload?.rows?.length > 0 && (
                    <button type="button" style={{ cursor:"pointer", background:"#e7eef8", color:"#345a8d", border:"none", padding:"8px 16px", borderRadius:8, fontSize:13.5, fontWeight:500 }}
                        onClick={async ()=>{if(window.confirm("Voulez-vous vraiment vider toutes les données de pointage importées ?")){try { await deleteRequest({ url: `${API_BASE_URL}/api/pointage-import`, token: sessionStorage.getItem("token") }); localStorage.removeItem(POINTAGE_STORAGE_KEY); setPointagePayload(null); } catch (error) { setPointageImportError("Suppression impossible."); }}}}>
                        {t("Vider les données")}
                    </button>
                )}
            </div>
            {pointageImportError ? <div style={{ color:"#345a8d", fontSize:13, marginTop:10, width:"100%" }}>{pointageImportError}</div> : null}
        </div>
    );

    const renderPointageDetail = (employee) => {
        const monthDays = buildMonthDays(pointagePeriod);
        const blanks = monthDays.length ? (new Date(`${monthDays[0]}T00:00:00`).getDay()+6)%7 : 0;
        return (
            <>
                <button type="button" className="daPointageBack" onClick={()=>setSelectedPointageEmployee(null)}>
                    <ArrowLeft size={16} /> {t("Retour à la liste")}
                </button>
                <div className="daPointageProfile">
                    <div className="daPointageAvatar">{employee.nomPrenom.split(" ").slice(0,2).map(p=>p[0]).join("")}</div>
                    <div>
                        <h3>{employee.nomPrenom}</h3>
                        <p>Matricule {employee.matricule} · {employee.section} · {pointagePeriod||"Période importée"}</p>
                    </div>
                    <div className="daPunctualityRing" style={{"--score":`${employee.presenceRate*3.6}deg`}}>
                        <span>{employee.presenceRate}%</span>
                    </div>
                </div>
                <div className="daGrid daGrid--4">
                    <StatCard title="Jours travaillés / attendus" value={`${employee.workedDays}/${employee.expectedDays}`} icon={<UserCheck size={20}/>} color={COLORS.success} />
                    <StatCard title="Absences"       value={employee.absences}                  icon={<UserX size={20}/>}        color={COLORS.error}  />
                    <StatCard title="Heures pointées" value={formatHours(employee.totalHours)}   icon={<Clock size={20}/>}        color={COLORS.info}   />
                    <StatCard title="Durée de présence moyenne" value={formatHours(employee.averageAmplitude)} icon={<CalendarRange size={20}/>} color={COLORS.primary} />
                </div>
                <ChartCard title="Calendrier mensuel du pointage">
                    <div className="daPointageLegend">
                        <span><i className="is-present"/> {t("Présent")}</span>
                        <span><i className="is-absent"/> {t("Absence")}</span>
                        <span><i className="is-anomalie"/> {t("Anomalie")}</span>
                        <span><i className="is-rest"/> {t("Repos")}</span>
                    </div>
                    <div className="daPointageCalendar">
                        {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d=><strong key={d}>{d}</strong>)}
                        {Array.from({length:blanks}).map((_,i)=><span key={`b-${i}`}/>)}
                        {monthDays.map(date=>{
                            const row = employee.byDate.get(date);
                            const status = row ? getPointageStatus(row) : (isExpectedWorkday(date,employee.section) ? "absence" : "repos");
                            return (
                                <button
                                    key={date}
                                    type="button"
                                    className={`daPointageDay is-${status}`}
                                    onClick={() => setPointageDayDetail({ date, row, employee })}
                                >
                                    <span>{Number(date.slice(8))}</span>
                                    <small>{row?.pointages?.[0]||""}</small>
                                </button>
                            );
                        })}
                    </div>
                </ChartCard>

                {/* ── Détail par intervalle ── */}
                {(() => {
                    const rangeDays = monthDays.filter(d =>
                        (!detailRangeStart || d >= detailRangeStart) && (!detailRangeEnd || d <= detailRangeEnd)
                    );
                    return (
                        <ChartCard title="Détails des pointages par jour">
                            <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:12, flexWrap:"wrap" }}>
                                <label style={{ fontSize:12, color:"#6b7280" }}>Du
                                    <input type="date" value={detailRangeStart} onChange={e=>setDetailRangeStart(e.target.value)}
                                           style={{ marginLeft:6, border:"1px solid #e5e7eb", borderRadius:6, padding:"4px 8px", fontSize:12.5 }} />
                                </label>
                                <label style={{ fontSize:12, color:"#6b7280" }}>Au
                                    <input type="date" value={detailRangeEnd} onChange={e=>setDetailRangeEnd(e.target.value)}
                                           style={{ marginLeft:6, border:"1px solid #e5e7eb", borderRadius:6, padding:"4px 8px", fontSize:12.5 }} />
                                </label>
                            </div>
                            <div style={{ overflowX:"auto" }}>
                                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                                    <thead>
                                    <tr>
                                        {["Date","Jour","Tous les pointages","Durée de présence","Statut"].map(h=>(
                                            <th key={h} style={{ textAlign:"left", padding:"6px 10px", fontWeight:600, color:"#6b7280", fontSize:12, borderBottom:"1px solid #f3f4f6" }}>{h}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rangeDays.map(d=>{
                                        const row = employee.byDate.get(d);
                                        const times = (row?.pointages||[]).map(timeToMinutes).filter(t=>t!==null);
                                        const amplitude = times.length>=2 ? (times[times.length-1]-times[0])/60 : 0;
                                        const status = row ? getPointageStatus(row) : (isExpectedWorkday(d,employee.section) ? "absence" : "repos");
                                        const statusLabel = status === "present" ? "Présence" : status === "anomalie" ? "Anomalie" : status === "absence" ? "Absence" : "Repos";
                                        return (
                                            <tr key={d} style={{ borderBottom:"1px solid #f9fafb", cursor: row ? "pointer" : "default" }}
                                                onClick={()=> row && setPointageDayDetail({ date:d, row, employee })}>
                                                <td style={{ padding:"7px 10px" }}>{d}</td>
                                                <td style={{ padding:"7px 10px", textTransform:"capitalize" }}>
                                                    {new Date(`${d}T00:00:00`).toLocaleDateString("fr-FR",{ weekday:"short" })}
                                                </td>
                                                <td style={{ padding:"7px 10px" }}>{row?.pointages?.join("  ·  ") || "—"}</td>
                                                <td style={{ padding:"7px 10px" }}>{amplitude ? formatHours(amplitude) : "—"}</td>
                                                <td style={{ padding:"7px 10px" }}><span className={`daPointageStatusBadge is-${status}`}>{statusLabel}</span></td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </ChartCard>
                    );
                })()}

                <PointageDayModal detail={pointageDayDetail} onClose={() => setPointageDayDetail(null)} />
            </>
        );
    };
    const renderPointage = () => {
        const selected = selectedPointageEmployee
            ? pointageAnalytics.allEmployees.find(e=>e.matricule===selectedPointageEmployee)
            : null;
        if (!pointagePayload?.rows?.length) {
            return (
                <>
                    <SectionTitle icon={<Clock size={18}/>}>Suivi des présences</SectionTitle>
                    {renderPointageImportPanel()}
                    <div className="daPointageEmpty">
                        <FileSpreadsheet size={34} />
                        <h3>{t("Aucun fichier de pointage importé")}</h3>
                        <p>{t("Importez le fichier Excel de pointage ici. Les KPI resteront affichés jusqu'au prochain fichier importé.")}</p>
                    </div>
                </>
            );
        }
        if (selected) return renderPointageDetail(selected);
        return (
            <>
                <SectionTitle icon={<Clock size={18}/>}>Suivi des présences</SectionTitle>
                <DateFilterFields dateDebut={dateDebut} dateFin={dateFin} onChangeDebut={setDateDebut} onChangeFin={setDateFin} />
                {renderPointageImportPanel()}
                <div className="daPointageFilters">
                    <select value={pointageSection} onChange={e=>setPointageSection(e.target.value)}>
                        <option value="all">{t("Toutes les sections")}</option>
                        {pointageAnalytics.sections.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={pointagePeriod} onChange={e=>setPointagePeriod(e.target.value)}>
                        <option value="">{t("Tous les mois")}</option>
                        {pointageAnalytics.periods.map(p=><option key={p} value={p}>{p}</option>)}
                    </select>
                    <label className="daPointageSearch">
                        <Search size={15}/>
                        <input value={pointageSearch} onChange={e=>setPointageSearch(e.target.value)} placeholder={t("Rechercher par nom")}/>
                    </label>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:15 }}>
                    <StatCard title="Effectif total"     value={pointageAnalytics.kpis.effectif}                    icon={<Users size={20}/>}      color="#2563a6" />
                    <StatCard title="Heures moy. / jour" value={`${pointageAnalytics.kpis.heures_moy} h`}           icon={<Clock size={20}/>}      color="#5b7fb0" />
                    <StatCard title="Taux de présence"   value={`${pointageAnalytics.kpis.presence_rate}%`}         icon={<UserCheck size={20}/>}  color="#27805c" />
                    <StatCard title="Anomalies (%)"      value={`${pointageAnalytics.kpis.anomalies_pct}%`} subtitle={`${pointageAnalytics.kpis.anomalies_tot} ${t("total")}`} icon={<AlertTriangle size={20}/>} color="#f4b400" />
                    <StatCard title="Heures totales"     value={`${pointageAnalytics.kpis.heures_totales.toLocaleString("fr-FR")} h`} icon={<Activity size={20}/>} color="#2a5ca9" />
                </div>
                <div className="daGrid daGrid--2" style={{ marginTop:20 }}>
                    <ChartCard title="Activité journalière">
                        <ResponsiveContainer width="100%" height={260}>
                            <AreaChart data={pointageAnalytics.daily}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="jour"/>
                                <YAxis/>
                                <RechartsTooltip/>
                                <Area type="monotone" dataKey="effectif" stroke="#2a5ca9" fill="#2a5ca9" fillOpacity={0.14} name="Effectif actif"/>
                                <Area type="monotone" dataKey="heures"   stroke="#6e91c1" fill="#6e91c1" fillOpacity={0.12} name="Heures moyennes"/>
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                    <ChartCard title="Répartition par section">
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={pointageAnalytics.sectionsData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="nom"/>
                                <YAxis/>
                                <RechartsTooltip/>
                                <Bar dataKey="effectif"  fill="#2a5ca9" name="Effectif"  radius={[4,4,0,0]}/>
                                <Bar dataKey="anomalies" fill="#f4b400" name="Anomalies" radius={[4,4,0,0]}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(320px,1fr))", gap:20, margin:"20px 0" }}>
                    <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:18 }}>
                        <h4 style={{ fontSize:15, fontWeight:600, color:"#1f2937", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                            <UserX size={18} color="#a85d5d"/> {t("Top absences")}
                        </h4>
                        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {pointageAnalytics.topAbsences.map((item,idx)=>(
                                <div key={idx} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:13, borderBottom:"1px solid #f3f4f6", paddingBottom:6 }}>
                                    <div>
                                        <strong style={{ color:"#374151" }}>{item.nom}</strong>
                                        <div style={{ fontSize:11, color:"#6b7280" }}>{item.section}</div>
                                    </div>
                                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                        <span style={{ fontSize:12.5, fontWeight:600, color:"#a85d5d" }}>{item.absences} j. abs</span>
                                        <button type="button" onClick={()=>setSelectedPointageEmployee(item.matricule)} style={{ background:"#f3f4f6", border:"none", padding:"4px 8px", borderRadius:4, cursor:"pointer", fontSize:11, color:"#4b5563" }}>{t("Détails")}</button>
                                    </div>
                                </div>
                            ))}
                            {pointageAnalytics.topAbsences.length===0 && <span style={{ color:"#9ca3af", fontSize:13 }}>{t("Aucune absence")}</span>}
                        </div>
                    </div>
                    <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:18 }}>
                        <h4 style={{ fontSize:15, fontWeight:600, color:"#1f2937", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                            <AlertTriangle size={18} color="#f4b400"/> {t("Top anomalies")}
                        </h4>
                        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {pointageAnalytics.topAnomalies.map((item,idx)=>(
                                <div key={idx} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:13, borderBottom:"1px solid #f3f4f6", paddingBottom:6 }}>
                                    <div>
                                        <strong style={{ color:"#374151" }}>{item.nom}</strong>
                                        <div style={{ fontSize:11, color:"#6b7280" }}>{item.section}</div>
                                    </div>
                                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                        <span style={{ fontSize:12.5, fontWeight:600, color:"#b78300" }}>{item.anomalies} anomalies</span>
                                        <button type="button" onClick={()=>setSelectedPointageEmployee(item.matricule)} style={{ background:"#f3f4f6", border:"none", padding:"4px 8px", borderRadius:4, cursor:"pointer", fontSize:11, color:"#4b5563" }}>{t("Détails")}</button>
                                    </div>
                                </div>
                            ))}
                            {pointageAnalytics.topAnomalies.length===0 && <span style={{ color:"#9ca3af", fontSize:13 }}>{t("Aucune anomalie")}</span>}
                        </div>
                    </div>
                </div>
                <div className="daPointageTableWrap">
                    <table className="daPointageTable">
                        <thead><tr><th>Collaborateur</th><th>Section</th><th>Taux de présence</th><th>Action</th></tr></thead>
                        <tbody>
                            {pointageAnalytics.employees.map(emp=>(
                                <tr key={emp.matricule} onClick={()=>setSelectedPointageEmployee(emp.matricule)}>
                                    <td><strong>{emp.nomPrenom}</strong><span>Matricule {emp.matricule}</span></td>
                                    <td>{emp.section}</td>
                                    <td>{emp.presenceRate}%</td>
                                    <td><button type="button" className="daPointageDetailBtn">{t("Détails")}</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    // ── États ──────────────────────────────────────────────────────────────────
    const renderOffboarding = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const inDays = (dateValue) => {
            if (!dateValue) return null;
            const date = new Date(`${String(dateValue).slice(0, 10)}T00:00:00`);
            return Number.isNaN(date.getTime()) ? null : Math.round((date - today) / 86400000);
        };
        const entityById = new Map(entites.map((entity) => [String(entity.id), entity.nom || entity.name || entity.libelle || "Entité non renseignée"]));
        const userById = new Map(users.map((user) => [String(user.id), user]));
        const reasonLabels = { DEMISSION: "Démission", FIN_DE_CONTRAT: "Fin de contrat", RETRAITE: "Retraite", LICENCIEMENT: "Licenciement", MUTATION: "Mutation" };
        const rows = offboardings.filter((item) => isWithinDateRange(item.dateDepart || item.date_depart, dateDebut, dateFin)).map((item) => {
            const user = userById.get(String(item.employeeId || item.employee_id)) || {};
            const entityId = user.entityId || user.entity_id || user.entiteId || user.entite_id || user.entite?.id;
            const progression = Math.max(0, Math.min(100, Number(item.progression) || 0));
            const steps = etapesByOffboarding[String(item.id)] || [];
            const pendingStepLabels = steps
                .filter((step) => String(step.statut || step.status || "").toUpperCase() !== "COMPLETE")
                .sort((a, b) => (a.ordreEtape ?? a.ordre_etape ?? 0) - (b.ordreEtape ?? b.ordre_etape ?? 0))
                .map((step) => step.nom || step.name || step.libelle || "Étape sans libellé");
            const departureDate = item.dateDepart || item.date_depart;
            const daysLeft = inDays(departureDate);
            return { ...item, employeeName: item.employeeName || item.employee_name || user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Collaborateur", departureDate, progression, daysLeft, status: daysLeft !== null && daysLeft < 0 ? "TERMINE" : "EN_COURS", pendingSteps: pendingStepLabels.length, pendingStepLabels, reason: reasonLabels[item.raisonDepart || item.raison_depart] || item.raisonDepart || item.raison_depart || "Non renseigné", entity: entityById.get(String(entityId)) || user.entityName || user.entiteName || "Non renseignée" };
        });
        const total = rows.length;
        const completed = rows.filter((row) => row.status === "TERMINE").length;
        const inProgress = rows.filter((row) => row.status === "EN_COURS").length;
        const planned = rows.filter((row) => row.progression === 0 && row.daysLeft !== null && row.daysLeft >= 0).length;
        const upcoming = rows.filter((row) => row.daysLeft !== null && row.daysLeft >= 0 && row.daysLeft <= 30).length;
        const averageProgress = total ? Math.round(rows.reduce((sum, row) => sum + row.progression, 0) / total) : 0;
        const pendingSteps = rows.reduce((sum, row) => sum + row.pendingSteps, 0);
        const atRisk = rows.filter((row) => row.daysLeft !== null && row.daysLeft >= 0 && row.daysLeft <= 7 && row.progression < 100);
        const countBy = (key) => Object.entries(rows.reduce((counts, row) => ({ ...counts, [row[key]]: (counts[row[key]] || 0) + 1 }), {})).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
        const reasonData = countBy("reason");
        const entityData = countBy("entity");
        const monthlyData = Object.entries(rows.reduce((counts, row) => { const month = row.departureDate ? String(row.departureDate).slice(0, 7) : null; if (month) counts[month] = (counts[month] || 0) + 1; return counts; }, {})).sort(([a], [b]) => a.localeCompare(b)).map(([month, count]) => ({ mois: new Date(`${month}-01T00:00:00`).toLocaleDateString("fr-FR", { month: "short", year: "numeric" }), count }));
        const detailColumns = [
            { key: "employeeName", label: "Collaborateur", render: (value) => <strong>{value}</strong> },
            { key: "departureDate", label: "Date de départ", render: (value) => value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString("fr-FR") : "–" },
            { key: "entity", label: "Entité / département" },
            { key: "reason", label: "Motif" },
            { key: "progression", label: "Progression", render: (value) => `${value}%` },
            { key: "pendingStepLabels", label: "Étapes à venir", render: (labels, row) => (
                <div style={{ minWidth: 190, display: "grid", gap: 5 }}>
                    <strong style={{ color: "#334155" }}>{row.pendingSteps} étape{row.pendingSteps > 1 ? "s" : ""}</strong>
                    {labels?.length ? <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{labels.map((label, index) => <span key={`${label}-${index}`} style={{ padding: "2px 7px", borderRadius: 999, background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 600 }}>{label}</span>)}</div> : <span style={{ color: "#16a34a", fontSize: 12 }}>Aucune étape à venir</span>}
                </div>
            ) },
        ];
        const openOffboardingDetails = (title, detailRows, emptyMsg = "Aucun offboarding correspondant") =>
            setSelectedCardModal({ title, rows: detailRows, columns: detailColumns, emptyMsg });
        const pendingStepRows = rows.flatMap((row) => (etapesByOffboarding[String(row.id)] || [])
            .filter((step) => String(step.statut || step.status || "").toUpperCase() !== "COMPLETE")
            .map((step) => ({
                id: step.id,
                employeeName: row.employeeName,
                stepName: step.nom || step.name || "Étape sans intitulé",
                stepOrder: step.ordreEtape ?? step.ordre_etape ?? "–",
                departureDate: row.departureDate,
                progression: row.progression,
            })));
        const openPendingStepsDetails = () => setSelectedCardModal({
            title: "Étapes d'offboarding en attente",
            rows: pendingStepRows,
            columns: [
                { key: "employeeName", label: "Collaborateur", render: (value) => <strong>{value}</strong> },
                { key: "stepName", label: "Étape en attente" },
                { key: "stepOrder", label: "Ordre" },
                { key: "departureDate", label: "Date de départ", render: (value) => value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString("fr-FR") : "–" },
                { key: "progression", label: "Progression", render: (value) => `${value}%` },
            ],
            emptyMsg: "Aucune étape en attente",
        });
        return (<>
            <SectionTitle icon={<UserX size={18} />}>Offboarding</SectionTitle>
            <DateFilterFields dateDebut={dateDebut} dateFin={dateFin} onChangeDebut={setDateDebut} onChangeFin={setDateFin} />
            <div className="daGrid daGrid--4">
                <StatCard title="Total des offboardings" value={total} icon={<Users size={20} />} color={COLORS.primary} onClick={() => openOffboardingDetails("Tous les offboardings", rows)} />
                <StatCard title="En cours" value={inProgress} icon={<Activity size={20} />} color={COLORS.info} onClick={() => openOffboardingDetails("Offboardings en cours", rows.filter((row) => row.status === "EN_COURS"))} />
                <StatCard title="Départs à venir (30 j)" value={upcoming} icon={<CalendarRange size={20} />} color={COLORS.warning} onClick={() => openOffboardingDetails("Départs à venir (30 jours)", rows.filter((row) => row.daysLeft !== null && row.daysLeft >= 0 && row.daysLeft <= 30))} />
                <StatCard title="Terminés" value={completed} icon={<CheckCircle2 size={20} />} color={COLORS.success} onClick={() => openOffboardingDetails("Offboardings terminés", rows.filter((row) => row.status === "TERMINE"))} />
            </div>
            <div className="daGrid daGrid--4" style={{ marginTop: 16 }}>
                <StatCard title="Complétion moyenne" value={`${averageProgress}%`} icon={<Gauge size={20} />} color={COLORS.teal} onClick={() => openOffboardingDetails("Détail de la complétion", [...rows].sort((a, b) => a.progression - b.progression))} />
                <StatCard title="Étapes en attente" value={pendingSteps} icon={<Clock size={20} />} color={COLORS.warning} onClick={openPendingStepsDetails} />
                <StatCard title="Offboardings à risque" value={atRisk.length} subtitle="Départ sous 7 jours" icon={<AlertTriangle size={20} />} color={COLORS.error} onClick={() => openOffboardingDetails("Offboardings à risque", atRisk)} />
                <StatCard title="Planifiés" value={planned} subtitle="Checklist non démarrée" icon={<CalendarCheck size={20} />} color={COLORS.purple} onClick={() => openOffboardingDetails("Offboardings planifiés", rows.filter((row) => row.progression === 0 && row.daysLeft !== null && row.daysLeft >= 0))} />
            </div>
            <div className="daGrid daGrid--2" style={{ marginTop: 20 }}>
                <ChartCard title="Répartition par motif de départ"><ResponsiveContainer width="100%" height={280}><PieChart><Pie data={reasonData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={({ name, value }) => `${name}: ${value}`}>{reasonData.map((_, index) => <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}</Pie><RechartsTooltip /></PieChart></ResponsiveContainer></ChartCard>
                <ChartCard title="Répartition par entité / département"><ResponsiveContainer width="100%" height={280}><BarChart data={entityData}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="name" /><YAxis allowDecimals={false} /><RechartsTooltip /><Bar dataKey="value" name="Départs" fill={COLORS.primary} radius={[8, 8, 0, 0]} /></BarChart></ResponsiveContainer></ChartCard>
            </div>
            <div className="daGrid daGrid--2" style={{ marginTop: 16 }}>
                <ChartCard title="Évolution mensuelle des départs"><ResponsiveContainer width="100%" height={260}><AreaChart data={monthlyData} margin={{ top: 30, right: 30, left: 10, bottom: 25 }}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="mois" label={{ value: "Mois", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} /><YAxis allowDecimals={false} label={{ value: "Départs", position: "top", offset: 10, style: { fontSize: 11, fill: "#94a3b8", fontWeight: 600 } }} /><RechartsTooltip /><Area type="monotone" dataKey="count" stroke={COLORS.secondary} fill={`${COLORS.secondary}20`} name="Départs" /></AreaChart></ResponsiveContainer></ChartCard>
                <ChartCard title="Alertes : départs proches">{atRisk.length ? <div style={{ display: "grid", gap: 10 }}>{atRisk.map((row) => <div key={row.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderBottom: "1px solid #eef0f3", paddingBottom: 10 }}><div><strong>{row.employeeName}</strong><div style={{ color: "#6b7280", fontSize: 13 }}>{row.entity} · {row.reason}</div></div><div style={{ textAlign: "right", color: COLORS.error, fontWeight: 700 }}>{row.daysLeft === 0 ? "Aujourd’hui" : `${row.daysLeft} j`}<div style={{ color: "#6b7280", fontSize: 13, fontWeight: 400 }}>{row.progression}% complété</div></div></div>)}</div> : <p style={{ color: "#6b7280" }}>Aucun offboarding à risque.</p>}</ChartCard>
            </div>
        </>);
    };

    const renderEtats = () => (
        <>
            <SectionTitle icon={<FileSpreadsheet size={18}/>}>États téléchargeables</SectionTitle>
            <p style={{ color:"#6b7280", fontSize:13.5, marginBottom:18 }}>
                Téléchargez les états de synthèse au format Excel en fonction des dates sélectionnées dans le filtre global.
            </p>
            <DateFilterFields dateDebut={dateDebut} dateFin={dateFin} onChangeDebut={setDateDebut} onChangeFin={setDateFin} />
            <div style={{ display:"grid", gap:28 }}>
                {EXCEL_REPORT_GROUPS_DYNAMIC.map((group) => (
                    <section key={group.title}>
                        <div style={{ marginBottom:12 }}>
                            <h3 style={{ margin:0, color:"#1f2937", fontSize:16, fontWeight:700 }}>{t(group.title)}</h3>
                            <p style={{ margin:"4px 0 0", color:"#6b7280", fontSize:12.5 }}>{t(group.description)}</p>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))", gap:16 }}>
                            {group.reports.map((report) => (
                                <ExcelStateCard key={report.id} title={report.title} description={report.description} color={report.color} onDownload={downloadHandlers[report.id]} />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </>
    );

    const TAB_RENDERERS = {
        // rh:           renderRH,
        conges:       renderConges,
        avances:      renderAvances,
        frais:        renderFrais,
        missions:     renderMissions,
        pointage:     renderPointage,
        offboarding:  renderOffboarding,
        attestations: renderAttestations,
        etats:        renderEtats,
    };

    return (
        <div className="daPage">
            <header className="daHeader daHeader--admin">
                <div>
                    <p className="daEyebrow"><ShieldCheck size={14} /> {t("Espace Admin RH")}</p>
                    <h1>{t("Suivi RH")}</h1>
                    <p>{t("Les indicateurs affichés couvrent les données de l'ensemble des collaborateurs de votre entreprise.")}</p>
                </div>
            </header>
            <div className="daTopTabs">
                {/*<button*/}
                {/*    type="button"*/}
                {/*    className={`daTopTab ${activeTab === "rh" ? "is-active" : ""}`}*/}
                {/*    onClick={() => setActiveTab("rh")}*/}
                {/*>*/}
                {/*    <Users size={16} /> RH & Effectifs*/}
                {/*</button>*/}

                <button
                    type="button"
                    className={`daTopTab ${isServiceTab(activeTab) ? "is-active" : ""}`}
                    onClick={() => { if (!isServiceTab(activeTab)) setActiveTab(SERVICE_TABS[0].id); }}
                >
                    <HandCoins size={16} /> {t("Demandes & Services")}
                </button>

                <button
                    type="button"
                    className={`daTopTab ${activeTab === "pointage" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("pointage")}
                >
                    <Clock size={16} /> {t("Suivi des présences")}
                </button>

                <button
                    type="button"
                    className={`daTopTab ${activeTab === "offboarding" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("offboarding")}
                >
                    <UserX size={16} /> {t("Offboarding")}
                </button>

                <button
                    type="button"
                    className={`daTopTab ${activeTab === "etats" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("etats")}
                >
                    <FileSpreadsheet size={16} /> {t("États")}
                </button>
            </div>

            {isServiceTab(activeTab) && (
                <div className="daSubTabs">
                    {SERVICE_TABS.map(tab => (
                        <button
                            key={tab.id}
                            type="button"
                            className={`daSubTab ${activeTab === tab.id ? "is-active" : ""}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span>{t(tab.label)}</span>
                        </button>
                    ))}
                </div>
            )}
            <main className="daMain">
                {TAB_RENDERERS[activeTab]?.()}
                <div className="daFooter" style={{ marginTop:32 }}>
                    <span>{t("Dernière mise à jour")} : {lastUpdated}</span>
                </div>
            </main>
            <CardDetailModal modalData={selectedCardModal} onClose={() => setSelectedCardModal(null)} />
        </div>
    );
}
